import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardApi, DashboardConfig } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useDashboardRealtime } from '../context/RealtimeContext';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import {
  EMPTY_TOTALS,
  PAGE_SIZE,
  ensureObject,
  formatDateTime,
  formatDetailValue,
  formatInboxSourceLabel,
  getComputedStageMeta,
  getProgressionCustomStatuses,
  getProgressionPercent,
  getProgressionStepType,
  isProgressionStepCompleteStatus,
  getProgressionStepStatusLabel,
  getProgressionStepTone,
  getTaskStepStateOptions,
  stringifyValue,
} from './inquiriesShared';
import {
  StageProgressPillContent,
  getStageProgressPillClassName,
} from './StageProgressPill';
import AppointmentDateTimeRangeField from './AppointmentDateTimeRangeField';
import LocationInput from './LocationInput';
import { getLocationAddress, locationToComparable, normalizeLocationValue } from './locationUtils';
import GoogleMeetSection from '../components/OnlineMeetingSection';
import './InquiriesPage.css';

const SummaryCard = ({ label, value, tone = 'default' }) => (
  <article className={`inquiries-summary-card ${tone !== 'default' ? `is-${tone}` : ''}`}>
    <span className="inquiries-summary-value">{value}</span>
    <span className="inquiries-summary-label">{label}</span>
  </article>
);

const QuickFilterButton = ({ active = false, color = null, count, label, onClick }) => (
  <button
    type="button"
    className={`inquiries-quick-filter${active ? ' is-active' : ''}`}
    onClick={onClick}
    aria-pressed={active}
  >
    <span>
      {color ? <i className="inquiries-product-swatch" style={{ backgroundColor: color }} aria-hidden="true" /> : null}
      {label}
    </span>
    {count !== undefined && count !== null ? <strong>{count}</strong> : null}
  </button>
);

const HIDDEN_LIST_COLUMN_IDS = new Set(['source', 'chat_id', 'created_at', 'updated_at', 'extra.form_id', 'progression', 'ai_summary']);
const LEGACY_LIST_COLUMN_IDS = new Set(['name', 'email', 'phone', 'subject', 'summary']);
const SUMMARY_COLUMN_ID = 'ai_summary';
const STAGE_COLUMN_ID = 'stage';
const ASSIGNEE_COLUMN_ID = 'assigned_to';
const FIXED_LEADING_COLUMN_IDS = [STAGE_COLUMN_ID, ASSIGNEE_COLUMN_ID];
const TRAILING_LIST_COLUMN_IDS = ['last_activity_at', 'submitted_at'];
const INLINE_STAGE_OPTIONS = [
  { value: 'new', label: 'Ny' },
  { value: 'in_progress', label: 'Pågår' },
  { value: 'resolved', label: 'Løst' },
];

const CUSTOM_STATUS_VALUE_PREFIX = 'custom:';
const PROGRESSION_STAGE_FILTER_MODE = 'progression_step';

const getInquiryStatusMenuOptions = (inquiry) => {
  const customStatuses = getProgressionCustomStatuses(inquiry?.progression);
  if (customStatuses.length === 0) return INLINE_STAGE_OPTIONS;
  const dynamicLabel = inquiry?.progression?.dynamic_stage_label || getComputedStageMeta(inquiry?.stage, {
    ...inquiry?.progression,
    custom_status: null,
  }).label;
  return [
    {
      value: '',
      label: `Følg kundeløp (${dynamicLabel})`,
      description: 'Dynamisk status',
    },
    ...customStatuses.map((status) => ({
      value: `${CUSTOM_STATUS_VALUE_PREFIX}${status.key}`,
      label: status.label,
      description: 'Marker som fullført',
    })),
  ];
};

const getInquirySelectedStatusValue = (inquiry) => (
  getProgressionCustomStatuses(inquiry?.progression).length > 0
    ? inquiry?.custom_status_key
      ? `${CUSTOM_STATUS_VALUE_PREFIX}${inquiry.custom_status_key}`
      : ''
    : inquiry?.stage || 'new'
);

const padDatePart = (value) => String(value).padStart(2, '0');

const formatDateTimeInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
};

const inputDateTimeToIso = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const getStepKey = (step) => String(step?.key || step?.id || '').trim();

const getProgressionStepComment = (step) => (
  typeof step?.metadata?.comment === 'string' ? step.metadata.comment : ''
);

const getAppointmentAttendanceType = (metadata = {}) => {
  const value = String(metadata?.attendance_type || metadata?.meeting_type || '').trim().toLowerCase();
  return ['digital', 'physical'].includes(value) ? value : '';
};

const getAppointmentLocationAddress = (metadata = {}) => {
  const location = metadata?.location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return '';
  return getLocationAddress(location);
};

const getStepAppointmentSource = (step) => {
  if (Array.isArray(step?.events) && step.events.length > 0) {
    return step.events[0];
  }
  return step;
};

const createProgressionStepDraft = (step, inquiryAssigneeId = null) => {
  const stepType = getProgressionStepType(step);
  if (stepType === 'checkbox') {
    return {
      taskStateKey: step?.task_state_key || step?.metadata?.task_state_key || 'todo',
      taskCompletedAt: step?.metadata?.task_completed_at || '',
      comment: getProgressionStepComment(step),
    };
  }
  if (stepType === 'log') {
    return {
      approved: step?.metadata?.approved === true,
      comment: getProgressionStepComment(step),
    };
  }
  const source = getStepAppointmentSource(step);
  const assignedAccountId = source?.assigned_account_id && source.assigned_account_id !== inquiryAssigneeId
    ? source.assigned_account_id
    : step?.assigned_account_id && step.assigned_account_id !== inquiryAssigneeId
      ? step.assigned_account_id
      : '';
  return {
    assignedAccountId,
    plannedStartAt: formatDateTimeInputValue(source?.planned_start_at || step?.planned_start_at),
    plannedEndAt: formatDateTimeInputValue(source?.planned_end_at || step?.planned_end_at),
    comment: getProgressionStepComment(step),
    attendanceType: getAppointmentAttendanceType(source?.metadata || step?.metadata || {}),
    meetingUrl: typeof source?.metadata?.meeting_url === 'string' ? source.metadata.meeting_url : typeof step?.metadata?.meeting_url === 'string' ? step.metadata.meeting_url : '',
    locationAddress: getAppointmentLocationAddress(source?.metadata || step?.metadata || {}),
    locationValue: normalizeLocationValue(source?.metadata?.location || step?.metadata?.location || null),
    meetingProvider: (source?.metadata?.use_google_meet || step?.metadata?.use_google_meet) ? 'google'
      : (source?.metadata?.use_microsoft_teams || step?.metadata?.use_microsoft_teams) ? 'microsoft'
      : null,
    googleImpersonatedEmail: source?.metadata?.google_impersonated_email || step?.metadata?.google_impersonated_email || '',
    meetingGuests: Array.isArray(source?.metadata?.google_meet_guests) ? source.metadata.google_meet_guests : Array.isArray(step?.metadata?.google_meet_guests) ? step.metadata.google_meet_guests : [],
    meetingDescription: source?.metadata?.google_meet_description || step?.metadata?.google_meet_description || '',
    googleMeetUrl: source?.metadata?.google_meet_url || step?.metadata?.google_meet_url || '',
    microsoftTeamsUrl: source?.metadata?.microsoft_teams_url || step?.metadata?.microsoft_teams_url || '',
  };
};

const createProgressionLogDraft = (step) => (
  (Array.isArray(step?.fields) ? step.fields : []).reduce((acc, field) => ({
    ...acc,
    [field.key]: field.type === 'boolean' ? false : '',
  }), {})
);

const normalizeTaskDraft = (draft) => ({
  assignedAccountId: String(draft?.assignedAccountId || '').trim(),
  plannedStartAt: String(draft?.plannedStartAt || '').trim(),
  plannedEndAt: String(draft?.plannedEndAt || '').trim(),
  comment: String(draft?.comment || ''),
  attendanceType: String(draft?.attendanceType || '').trim(),
  meetingUrl: String(draft?.meetingUrl || '').trim(),
  locationAddress: String(draft?.locationAddress || '').trim(),
  locationValueKey: locationToComparable(draft?.locationValue || draft?.locationAddress || null),
});

const isStepDraftDirty = (step, draft, inquiryAssigneeId = null) => {
  const stepType = getProgressionStepType(step);
  if (stepType === 'checkbox') {
    const saved = createProgressionStepDraft(step);
    return saved.taskStateKey !== String(draft?.taskStateKey || 'todo')
      || saved.comment !== String(draft?.comment || '');
  }
  if (stepType === 'log') {
    const saved = createProgressionStepDraft(step);
    return Boolean(saved.approved) !== Boolean(draft?.approved)
      || saved.comment !== String(draft?.comment || '');
  }
  const saved = normalizeTaskDraft(createProgressionStepDraft(step, inquiryAssigneeId));
  const current = normalizeTaskDraft(draft);
  return saved.assignedAccountId !== current.assignedAccountId
    || saved.plannedStartAt !== current.plannedStartAt
    || saved.plannedEndAt !== current.plannedEndAt
    || saved.comment !== current.comment
    || saved.attendanceType !== current.attendanceType
    || saved.meetingUrl !== current.meetingUrl
    || saved.locationAddress !== current.locationAddress
    || saved.locationValueKey !== current.locationValueKey;
};

const isLogDraftDirty = (draft, fields = []) => (
  (Array.isArray(fields) ? fields : []).some((field) => {
    const value = draft?.[field.key];
    if (field.type === 'boolean') return Boolean(value);
    return String(value ?? '').trim() !== '';
  })
);

const createManualInquiryDraft = () => ({
  inquiryTypeId: '',
  botProductId: '',
  assignedAccountId: '',
  stage: 'new',
  subject: '',
  attributeValues: {},
});

const stopRowNavigation = (event) => {
  event.stopPropagation();
};

const getInitials = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '?';
  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] || ''}${parts[parts.length - 1]?.[0] || ''}`.toUpperCase();
};

const resolveProfileImageUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('data:') || /^https?:\/\//i.test(trimmed)) return trimmed;
  try {
    return new URL(trimmed, DashboardConfig.apiBaseUrl).href;
  } catch {
    return trimmed;
  }
};

const getInquiryAssignee = (inquiry, { unassignedLabel = 'Ufordelt' } = {}) => {
  const member = inquiry?.assigned_member || null;
  const label = member?.display_name || member?.displayName || member?.email || unassignedLabel;
  return {
    label,
    assigned: Boolean(member),
    profileImageUrl: resolveProfileImageUrl(
      member?.profilePictureUrl
      || member?.profile_picture_url
      || member?.avatarUrl
      || member?.avatar_url
      || member?.photoUrl
      || member?.photo_url
      || null,
    ),
  };
};

const getInquiryStatusDotTone = (inquiry) => {
  const stageMeta = getComputedStageMeta(inquiry?.stage, inquiry?.progression);
  if (stageMeta?.className === 'inquiry-workflow-resolved') return 'resolved';
  if (stageMeta?.className === 'inquiry-workflow-in-progress') return 'in-progress';
  return 'new';
};

const hasVisibleProgression = (progression) => (
  Boolean(progression?.configured && getProgressionPercent(progression) > 0 && Array.isArray(progression?.steps) && progression.steps.length > 0)
);

const AssigneePillContent = ({ assignee }) => (
  <>
    <span className="inquiry-stage-assignee-avatar" aria-hidden="true">
      {assignee.profileImageUrl ? (
        <img src={assignee.profileImageUrl} alt="" />
      ) : (
        getInitials(assignee.label)
      )}
    </span>
    <span>{assignee.label}</span>
  </>
);

const useFloatingMenuPosition = (isOpen, anchorRef, menuRef, options = {}) => {
  const [style, setStyle] = useState(null);
  const minWidth = options.minWidth || 192;
  const maxWidth = options.maxWidth || 320;

  useLayoutEffect(() => {
    if (!isOpen) {
      setStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor || typeof window === 'undefined') return;

      const rect = anchor.getBoundingClientRect();
      const viewportWidth = window.innerWidth || 0;
      const viewportHeight = window.innerHeight || 0;
      const gap = 6;
      const safeInset = 12;
      const resolvedMaxWidth = Math.max(minWidth, Math.min(maxWidth, viewportWidth - safeInset * 2));
      const resolvedMinWidth = Math.min(Math.max(rect.width, minWidth), resolvedMaxWidth);
      const measuredHeight = menuRef.current?.offsetHeight || 220;
      const measuredWidth = menuRef.current?.offsetWidth || resolvedMinWidth;
      const resolvedWidth = Math.min(Math.max(measuredWidth, resolvedMinWidth), resolvedMaxWidth);
      const canOpenUp = rect.top - measuredHeight - gap > safeInset;
      const shouldOpenUp = rect.bottom + measuredHeight + gap > viewportHeight - safeInset && canOpenUp;
      const top = shouldOpenUp
        ? Math.max(safeInset, rect.top - measuredHeight - gap)
        : Math.min(rect.bottom + gap, Math.max(safeInset, viewportHeight - safeInset - measuredHeight));
      const left = Math.min(
        Math.max(safeInset, rect.left),
        Math.max(safeInset, viewportWidth - safeInset - resolvedWidth),
      );

      setStyle({
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        minWidth: `${Math.round(resolvedMinWidth)}px`,
        maxWidth: `${Math.round(resolvedMaxWidth)}px`,
      });
    };

    updatePosition();
    const frame = window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef, isOpen, maxWidth, menuRef, minWidth]);

  return style;
};

const SortArrowIcon = ({ direction }) => (
  <svg
    className="inquiries-sort-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {direction === 'asc' ? (
      <>
        <path d="M12 19V5" />
        <path d="m5 12 7-7 7 7" />
      </>
    ) : (
      <>
        <path d="M12 5v14" />
        <path d="m19 12-7 7-7-7" />
      </>
    )}
  </svg>
);

const SearchIcon = () => (
  <svg
    className="inquiries-filter-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const ExpandRowIcon = ({ expanded = false }) => (
  <svg
    className="inquiries-row-expand-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {expanded ? <path d="m6 9 6 6 6-6" /> : <path d="m9 6 6 6-6 6" />}
  </svg>
);

const InlineOptionMenu = ({
  label,
  buttonClassName,
  disabled = false,
  isOpen = false,
  isLoading = false,
  options = [],
  selectedValue = '',
  children = null,
  onOpen,
  onSelect,
}) => {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuStyle = useFloatingMenuPosition(isOpen, buttonRef, menuRef, {
    minWidth: 192,
    maxWidth: 320,
  });

  const menu = isOpen && typeof document !== 'undefined'
    ? createPortal(
      <span
        ref={menuRef}
        className="inquiries-floating-menu inquiries-inline-menu"
        role="listbox"
        style={menuStyle || undefined}
        onClick={stopRowNavigation}
        onMouseDown={stopRowNavigation}
      >
        {options.map((option) => {
          const isSelected = String(option.value ?? '') === String(selectedValue ?? '');
          return (
            <button
              key={`${option.value}-${option.label}`}
              type="button"
              className={`inquiries-inline-menu-option ${isSelected ? 'is-selected' : ''}`}
              role="option"
              aria-selected={isSelected}
              disabled={disabled}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onSelect(option.value);
              }}
            >
              <span>{option.label}</span>
              {option.description ? <small>{option.description}</small> : null}
            </button>
          );
        })}
      </span>,
      document.body,
    )
    : null;

  return (
    <span className="inquiries-inline-menu-anchor" onClick={stopRowNavigation}>
      <button
        ref={buttonRef}
        type="button"
        className={buttonClassName}
        disabled={disabled}
        onClick={onOpen}
        onKeyDown={stopRowNavigation}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {isLoading ? 'Lagrer…' : (children || label)}
      </button>
      {menu}
    </span>
  );
};

const ColumnFilterMenu = ({
  column,
  variant,
  options = [],
  isOpen,
  hasActiveFilter,
  draftValue,
  activeValue,
  activeMode,
  onOpen,
  onDraftChange,
  onApply,
  onClear,
  onSelect,
}) => {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [selectSearchQuery, setSelectSearchQuery] = useState('');
  const menuStyle = useFloatingMenuPosition(isOpen, buttonRef, menuRef, {
    minWidth: variant === 'select' ? 220 : 260,
    maxWidth: 360,
  });
  const isStatusColumn = column?.id === STAGE_COLUMN_ID;
  const baseOptions = isStatusColumn ? options.filter((option) => option.group !== 'progression') : options;
  const progressionOptions = isStatusColumn ? options.filter((option) => option.group === 'progression') : [];
  const normalizedSelectSearch = normalizeFilterSearchText(selectSearchQuery);
  const filteredProgressionOptions = normalizedSelectSearch
    ? progressionOptions.filter((option) => normalizeFilterSearchText(option.label).includes(normalizedSelectSearch))
    : progressionOptions;

  useEffect(() => {
    if (!isOpen) setSelectSearchQuery('');
  }, [isOpen]);

  const renderSelectOption = (option) => {
    const optionMode = option.mode || 'equals';
    const isSelected = String(option.value ?? '') === String(activeValue ?? '')
      && optionMode === (activeMode || 'equals');
    return (
      <button
        key={`${column.id}-${optionMode}-${option.value}-${option.label}`}
        type="button"
        className={`inquiries-inline-menu-option ${isSelected ? 'is-selected' : ''}`}
        role="option"
        aria-selected={isSelected}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onSelect(option.value, optionMode);
        }}
      >
        {option.label}
      </button>
    );
  };

  const menu = isOpen && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={menuRef}
        className="inquiries-floating-menu inquiries-column-filter-menu"
        style={menuStyle || undefined}
        onClick={stopRowNavigation}
        onMouseDown={stopRowNavigation}
      >
        <div className="inquiries-column-filter-head">
          <span>{column.label}</span>
          {hasActiveFilter ? (
            <button type="button" onClick={onClear}>
              Fjern
            </button>
          ) : null}
        </div>

        {variant === 'select' ? (
          <div className="inquiries-column-filter-options" role="listbox">
            {options.length === 0 ? (
              <p className="input-hint">Ingen verdier tilgjengelig.</p>
            ) : isStatusColumn ? (
              <>
                <div className="inquiries-column-filter-section">
                  {baseOptions.map(renderSelectOption)}
                </div>
                <div className="inquiries-column-filter-section">
                  <span className="inquiries-column-filter-section-label">Kundeløp</span>
                  <label className="inquiries-column-filter-search">
                    <SearchIcon />
                    <input
                      type="search"
                      value={selectSearchQuery}
                      onChange={(event) => setSelectSearchQuery(event.target.value)}
                      placeholder="Søk i steg"
                      aria-label="Søk i kundeløpsteg"
                      onClick={stopRowNavigation}
                      onKeyDown={stopRowNavigation}
                    />
                  </label>
                  <div className="inquiries-column-filter-scroll">
                    {filteredProgressionOptions.length > 0 ? (
                      filteredProgressionOptions.map(renderSelectOption)
                    ) : (
                      <p className="input-hint">Ingen steg matcher søket.</p>
                    )}
                  </div>
                </div>
              </>
            ) : options.map(renderSelectOption)}
          </div>
        ) : (
          <form
            className="inquiries-column-filter-form"
            onSubmit={(event) => {
              event.preventDefault();
              onApply();
            }}
          >
            <input
              className="input input--sm"
              type="search"
              value={draftValue}
              onChange={(event) => onDraftChange(event.target.value)}
              placeholder="Søk i kolonnen"
              autoFocus
            />
            <div className="inquiries-column-filter-actions">
              <button type="button" className="button button-secondary" onClick={onClear}>
                Nullstill
              </button>
              <button type="submit" className="button button-primary">
                Søk
              </button>
            </div>
          </form>
        )}
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`inquiries-column-filter-button ${hasActiveFilter ? 'is-active' : ''}`}
        onClick={onOpen}
        aria-label={`Filtrer ${column.label}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <SearchIcon />
      </button>
      {menu}
    </>
  );
};

const normalizeColumnList = (columns) => {
  const normalized = [];
  const seen = new Set();

  (Array.isArray(columns) ? columns : []).forEach((column) => {
    const columnId = String(column?.id || '').trim();
    if (!columnId || HIDDEN_LIST_COLUMN_IDS.has(columnId) || LEGACY_LIST_COLUMN_IDS.has(columnId) || seen.has(columnId)) {
      return;
    }
    seen.add(columnId);
    if (columnId === STAGE_COLUMN_ID) {
      normalized.push({ ...column, label: 'Status' });
    } else if (columnId === ASSIGNEE_COLUMN_ID) {
      normalized.push({ ...column, label: 'Saksbehandler' });
    } else {
      normalized.push(column);
    }
  });

  return normalized;
};

const getColumnOrderRank = (columnId, columnsById) => {
  const column = columnsById.get(columnId);
  if (columnId === STAGE_COLUMN_ID) return 0;
  if (columnId === ASSIGNEE_COLUMN_ID) return 1;
  if (columnId === 'product') return 2;
  if (column?.kind === 'attribute' && column.productSpecific !== true) return 10;
  if (column?.kind === 'attribute' && column.productSpecific === true) return 20;
  if (columnId === SUMMARY_COLUMN_ID) return 30;
  if (TRAILING_LIST_COLUMN_IDS.includes(columnId)) return 40 + TRAILING_LIST_COLUMN_IDS.indexOf(columnId);
  if (column?.kind === 'extra') return 60;
  return 50;
};

const orderColumnIds = (columnIds, columnsById, fallbackIds = [], options = {}) => {
  const useDefaultRank = options.useDefaultRank !== false;
  const normalizedIds = [];
  const seen = new Set();

  (Array.isArray(columnIds) ? columnIds : []).forEach((columnId) => {
    const normalizedId = String(columnId || '').trim();
    if (!normalizedId || !columnsById.has(normalizedId) || seen.has(normalizedId)) return;
    seen.add(normalizedId);
    normalizedIds.push(normalizedId);
  });

  FIXED_LEADING_COLUMN_IDS
    .slice()
    .reverse()
    .forEach((columnId) => {
      if (!seen.has(columnId) && columnsById.has(columnId)) {
        normalizedIds.unshift(columnId);
        seen.add(columnId);
      }
    });
  const originalRank = new Map(normalizedIds.map((columnId, index) => [columnId, index]));
  const ordered = useDefaultRank
    ? [...normalizedIds].sort((left, right) => {
      const leftRank = getColumnOrderRank(left, columnsById);
      const rightRank = getColumnOrderRank(right, columnsById);
      if (leftRank !== rightRank) return leftRank - rightRank;
      return (originalRank.get(left) || 0) - (originalRank.get(right) || 0);
    })
    : [...normalizedIds];

  const fixedLeadingIds = FIXED_LEADING_COLUMN_IDS.filter((columnId) => columnsById.has(columnId));
  if (fixedLeadingIds.length > 0) {
    return [
      ...fixedLeadingIds,
      ...ordered.filter((columnId) => !fixedLeadingIds.includes(columnId)),
    ];
  }

  if (ordered.length > 0) {
    return ordered;
  }

  return fallbackIds.filter((columnId, index, allIds) => allIds.indexOf(columnId) === index);
};

const getColumnClassName = (column) => {
  if (!column?.id) return '';
  if (column.id === STAGE_COLUMN_ID) return 'inquiries-column-stage';
  if (column.id === ASSIGNEE_COLUMN_ID) return 'inquiries-column-assignee';
  if (column.id === SUMMARY_COLUMN_ID) return 'inquiries-column-summary';
  return '';
};

const getInquiryFieldValue = (inquiry, column) => {
  if (!inquiry || !column) return null;
  if (column.kind === 'attribute') {
    return ensureObject(inquiry.attribute_values)[column.key];
  }
  if (column.kind === 'extra') {
    return ensureObject(inquiry.extra)[column.key];
  }

  switch (column.id) {
    case 'name':
      return inquiry.display_name || inquiry.name || (inquiry.id ? `Henvendelse #${inquiry.id}` : null);
    case 'assigned_to':
      return inquiry.assigned_member?.display_name || inquiry.assigned_member?.email || null;
    case SUMMARY_COLUMN_ID:
      return inquiry.ai_summary || null;
    case 'submitted_at':
      return inquiry.submitted_at || inquiry.created_at || null;
    default:
      return inquiry[column.id];
  }
};

const getInquiryAttributeDefinition = (inquiry, column) => {
  if (!inquiry || column?.kind !== 'attribute' || !column.key) return null;
  const fields = Array.isArray(inquiry.resolved_attribute_definitions)
    ? inquiry.resolved_attribute_definitions
    : [];
  return fields.find((field) => field?.key === column.key) || null;
};

const isEditableSelectAttribute = (inquiry, column) => {
  const field = getInquiryAttributeDefinition(inquiry, column);
  return field?.type === 'select' && Array.isArray(field.options) && field.options.length > 0;
};

const getColumnDefinitionFromRows = (inquiries, column) => {
  if (column?.kind !== 'attribute') return null;
  for (const inquiry of Array.isArray(inquiries) ? inquiries : []) {
    const definition = getInquiryAttributeDefinition(inquiry, column);
    if (definition) return definition;
  }
  return null;
};

const uniqueOptionValues = (entries) => {
  const seen = new Set();
  return (Array.isArray(entries) ? entries : [])
    .map((entry) => ({
      value: entry?.value ?? entry,
      label: entry?.label ?? entry?.value ?? entry,
      mode: entry?.mode,
    }))
    .filter((entry) => {
      const value = String(entry.value ?? '').trim();
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

const normalizeFilterSearchText = (value) => String(value || '').trim().toLocaleLowerCase('nb-NO');

const progressionHasActivity = (progression) => {
  const steps = Array.isArray(progression?.steps) ? progression.steps : [];
  return steps.some((step) => {
    const source = getStepAppointmentSource(step);
    return (
      step?.touched === true
      || step?.status !== 'todo'
      || Number(step?.log_count || 0) > 0
      || Boolean(
        step?.planned_start_at || step?.planned_end_at
        || source?.planned_start_at || source?.planned_end_at,
      )
    );
  });
};

const getCurrentProgressionStep = (progression) => {
  const steps = Array.isArray(progression?.steps) ? progression.steps : [];
  if (!progression?.configured || steps.length === 0 || !progressionHasActivity(progression)) return null;
  return steps.find((step) => step?.required !== false && !isProgressionStepCompleteStatus(step)) || null;
};

const normalizeProgressionStageFilterOptions = (entries) => {
  const seen = new Set();
  return (Array.isArray(entries) ? entries : [])
    .map((entry, index) => {
      const key = String(entry?.key || entry?.step_key || entry?.value || '').trim();
      if (!key || seen.has(key)) return null;
      seen.add(key);
      return {
        value: key,
        label: entry?.label || entry?.step_label || key,
        mode: PROGRESSION_STAGE_FILTER_MODE,
        group: 'progression',
        sortOrder: Number.isFinite(Number(entry?.sortOrder ?? entry?.sort_order))
          ? Number(entry?.sortOrder ?? entry?.sort_order)
          : index,
      };
    })
    .filter(Boolean)
    .sort((left, right) => (
      Number(left.sortOrder || 0) - Number(right.sortOrder || 0)
      || left.label.localeCompare(right.label, 'nb')
    ));
};

const getProgressionStageFilterOptionsFromInquiries = (inquiries) => normalizeProgressionStageFilterOptions(
  (Array.isArray(inquiries) ? inquiries : []).flatMap((inquiry) => (
    Array.isArray(inquiry?.progression?.steps)
      ? inquiry.progression.steps
        .filter((step) => step && step.archived !== true)
        .map((step) => ({
          key: getStepKey(step),
          label: step?.label,
          sortOrder: step?.sortOrder ?? step?.sort_order,
        }))
      : []
  )),
);

const getColumnFilterVariant = (column, inquiries) => {
  if (!column?.id) return 'text';
  if (['stage', 'assigned_to', 'product'].includes(column.id)) return 'select';
  const definition = getColumnDefinitionFromRows(inquiries, column);
  if (definition?.type === 'select' || definition?.sourceConfig?.source === 'products') return 'select';
  return 'text';
};

const getColumnFilterOptions = (column, inquiries, summaryAssignees, summaryProducts, summaryProgressionStages = []) => {
  if (!column?.id) return [];
  if (column.id === 'stage') {
    const progressionStages = summaryProgressionStages.length > 0
      ? summaryProgressionStages
      : getProgressionStageFilterOptionsFromInquiries(inquiries);
    return [
      ...INLINE_STAGE_OPTIONS.map((option) => ({ ...option, group: 'base', mode: 'equals' })),
      ...progressionStages,
    ];
  }
  if (column.id === 'assigned_to') {
    return [
      { value: 'unassigned', label: 'Ufordelt' },
      ...summaryAssignees.map((assignee) => ({
        value: assignee.account_id,
        label: assignee.display_name || assignee.email,
      })),
    ];
  }
  if (column.id === 'product') {
    return uniqueOptionValues(summaryProducts.map((product) => ({ value: product.name, label: product.name })));
  }

  const definition = getColumnDefinitionFromRows(inquiries, column);
  if (definition?.type === 'select' && Array.isArray(definition.options)) {
    return uniqueOptionValues(definition.options);
  }
  return uniqueOptionValues(inquiries.map((inquiry) => stringifyValue(getInquiryFieldValue(inquiry, column))));
};

const normalizeComparableValue = (value, column) => {
  if (value === null || value === undefined || value === '') return null;
  if (['submitted_at', 'created_at', 'updated_at', 'last_activity_at'].includes(column?.id)) {
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }
  if (typeof value === 'number') return value;
  return stringifyValue(value).toLocaleLowerCase('nb-NO');
};

const getDynamicStageSortRank = (inquiry) => {
  const progression = inquiry?.progression || {};
  const percent = getProgressionPercent(progression);
  if (
    inquiry?.custom_status_key
    || progression.custom_status_key
    || progression.custom_status
    || inquiry?.stage === 'resolved'
    || (percent !== null && percent >= 100)
  ) {
    return 100000;
  }

  const steps = Array.isArray(progression.steps) ? progression.steps : [];
  if (progression.configured && steps.length > 0) {
    const currentStep = getCurrentProgressionStep(progression);
    if (!currentStep) return progressionHasActivity(progression) ? 100000 : 0;
    const configuredOrder = Number(currentStep.sortOrder ?? currentStep.sort_order);
    if (Number.isFinite(configuredOrder)) return configuredOrder + 1;
    return steps.indexOf(currentStep) + 1;
  }

  if (inquiry?.stage === 'in_progress') return 50000;
  return 0;
};

const compareInquiriesByColumn = (left, right, column, direction) => {
  if (column?.id === STAGE_COLUMN_ID) {
    const comparison = getDynamicStageSortRank(left) - getDynamicStageSortRank(right);
    const directedComparison = direction === 'asc' ? comparison : comparison * -1;
    return directedComparison === 0 ? Number(right.id || 0) - Number(left.id || 0) : directedComparison;
  }
  const leftValue = normalizeComparableValue(getInquiryFieldValue(left, column), column);
  const rightValue = normalizeComparableValue(getInquiryFieldValue(right, column), column);

  if (leftValue === null && rightValue === null) return Number(right.id || 0) - Number(left.id || 0);
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;

  let comparison = 0;
  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    comparison = leftValue - rightValue;
  } else {
    comparison = String(leftValue).localeCompare(String(rightValue), 'nb', { numeric: true });
  }
  if (comparison === 0) {
    comparison = Number(right.id || 0) - Number(left.id || 0);
  }
  return direction === 'asc' ? comparison : comparison * -1;
};

const rowMatchesColumnFilter = (inquiry, column, filter) => {
  if (!column || !filter?.value) return true;
  if (column.id === 'assigned_to' && filter.value === 'unassigned') {
    return !inquiry.assigned_to;
  }
  if (column.id === 'stage' && filter.mode === PROGRESSION_STAGE_FILTER_MODE) {
    return getStepKey(getCurrentProgressionStep(inquiry?.progression)) === String(filter.value || '').trim();
  }
  const rawValue = getInquiryFieldValue(inquiry, column);
  const value = stringifyValue(rawValue).toLocaleLowerCase('nb-NO');
  const filterValue = String(filter.value || '').toLocaleLowerCase('nb-NO');
  if (filter.mode === 'equals') {
    return value === filterValue;
  }
  return value.includes(filterValue);
};

const getInquiryDetailSummary = (inquiry) => {
  const value = inquiry?.ai_summary || inquiry?.summary || '';
  return stringifyValue(value);
};

const getInquiryDetailAttributes = (inquiry) => {
  const values = ensureObject(inquiry?.attribute_values);
  const definitions = Array.isArray(inquiry?.resolved_attribute_definitions)
    ? inquiry.resolved_attribute_definitions
    : [];
  const rows = [];
  const seen = new Set();

  definitions.forEach((field) => {
    const key = String(field?.key || '').trim();
    if (!key || seen.has(key) || !Object.prototype.hasOwnProperty.call(values, key)) return;
    const value = stringifyValue(values[key]);
    if (!value || value === '—') return;
    seen.add(key);
    rows.push({
      key,
      label: field.label || key,
      value,
    });
  });

  Object.entries(values).forEach(([key, rawValue]) => {
    if (!key || seen.has(key)) return;
    const value = stringifyValue(rawValue);
    if (!value || value === '—') return;
    rows.push({
      key,
      label: key,
      value,
    });
  });

  return rows;
};

const getProgressionStepDateLabel = (step, stepLogs = []) => {
  const latestLog = Array.isArray(stepLogs) ? stepLogs[0] : null;
  if (latestLog?.created_at) return `Logget ${formatDateTime(latestLog.created_at)}`;
  if (step?.completed_at) return `Logget ${formatDateTime(step.completed_at)}`;
  if (step?.metadata?.approved_at) return `Logget ${formatDateTime(step.metadata.approved_at)}`;
  const source = getStepAppointmentSource(step);
  if (source?.planned_start_at) return `Planlagt ${formatDateTime(source.planned_start_at)}`;
  if (source?.planned_end_at) return `Planlagt ${formatDateTime(source.planned_end_at)}`;
  if (step?.planned_start_at) return `Planlagt ${formatDateTime(step.planned_start_at)}`;
  if (step?.planned_end_at) return `Planlagt ${formatDateTime(step.planned_end_at)}`;
  if (step?.updated_at && step?.status && step.status !== 'todo') return `Logget ${formatDateTime(step.updated_at)}`;
  return '';
};

const renderLogDraftInput = ({
  field,
  stepKey,
  value,
  disabled,
  onChange,
}) => {
  if (field.type === 'text') {
    return (
      <textarea
        className="input inquiries-note-textarea inquiry-detail-casework-textarea"
        value={String(value ?? '')}
        disabled={disabled}
        onChange={(event) => onChange(stepKey, field.key, event.target.value)}
        aria-label={field.label}
      />
    );
  }
  if (field.type === 'boolean') {
    return (
      <button
        type="button"
        className={`inquiry-detail-boolean-toggle ${Boolean(value) ? 'is-yes' : 'is-no'}`}
        aria-pressed={Boolean(value)}
        disabled={disabled}
        onClick={() => onChange(stepKey, field.key, !Boolean(value))}
      >
        {Boolean(value) ? 'Ja' : 'Nei'}
      </button>
    );
  }
  if (field.type === 'select') {
    return (
      <select
        className="select"
        value={String(value ?? '')}
        disabled={disabled}
        onChange={(event) => onChange(stepKey, field.key, event.target.value)}
        aria-label={field.label}
      >
        <option value="">Velg</option>
        {(field.options || []).map((option) => (
          <option key={`${stepKey}-${field.key}-${option}`} value={option}>{option}</option>
        ))}
      </select>
    );
  }
  if (field.type === 'location') {
    return (
      <LocationInput
        value={value}
        disabled={disabled}
        onChange={(nextValue) => onChange(stepKey, field.key, nextValue)}
        aria-label={field.label}
      />
    );
  }
  return (
    <input
      className="input"
      type={field.type === 'date' ? 'date' : field.type === 'datetime' ? 'datetime-local' : field.type === 'integer' ? 'number' : 'text'}
      value={String(value ?? '')}
      disabled={disabled}
      onChange={(event) => onChange(stepKey, field.key, event.target.value)}
      aria-label={field.label}
    />
  );
};

const InquiryProgressionTimeline = ({
  activeInquiry,
  companyId,
  canManageInbox,
  assignees,
  progressionLogs,
  selectedStepKey,
  progressionDrafts,
  progressionLogDrafts,
  progressionLogDraftRows,
  progressionLogEditIds,
  progressionSavingKey,
  progressionError,
  detailLoading,
  onToggleStep,
  onUpdateDraft,
  onUpdateLogDraft,
  onStartLogDraft,
  onStartLogEdit,
  onDeleteLog,
  onDiscardChanges,
  onSavePanel,
}) => {
  const progression = activeInquiry?.progression;
  const steps = Array.isArray(progression?.steps) ? progression.steps : [];
  const percent = getProgressionPercent(progression);
  const progressionLogsByStep = useMemo(() => {
    const map = new Map();
    (Array.isArray(progressionLogs) ? progressionLogs : []).forEach((entry) => {
      if (!map.has(entry.step_key)) map.set(entry.step_key, []);
      map.get(entry.step_key).push(entry);
    });
    return map;
  }, [progressionLogs]);
  const selectedStep = steps.find((step) => getStepKey(step) === selectedStepKey) || null;
  const selectedStepType = selectedStep ? getProgressionStepType(selectedStep) : null;
  const selectedStepDraft = selectedStep ? progressionDrafts[getStepKey(selectedStep)] || {} : {};
  const selectedLogDraft = selectedStep ? progressionLogDrafts[getStepKey(selectedStep)] || {} : {};
  const selectedLogDraftOpen = selectedStep ? Boolean(progressionLogDraftRows[getStepKey(selectedStep)]) : false;
  const selectedLogEditId = selectedStep ? progressionLogEditIds[getStepKey(selectedStep)] || null : null;
  const selectedStepLogs = selectedStep ? progressionLogsByStep.get(getStepKey(selectedStep)) || [] : [];
  const selectedStepSaving = selectedStep
    ? progressionSavingKey === getStepKey(selectedStep)
      || progressionSavingKey === `${getStepKey(selectedStep)}:log`
    : false;
  const selectedStepDirty = selectedStep
    ? selectedStepType === 'log'
      ? isStepDraftDirty(selectedStep, selectedStepDraft, activeInquiry?.assigned_to || null)
        || selectedLogDraftOpen
        || Boolean(selectedLogEditId)
        || isLogDraftDirty(selectedLogDraft, selectedStep.fields)
      : isStepDraftDirty(selectedStep, selectedStepDraft, activeInquiry?.assigned_to || null)
    : false;
  const assignedMemberLabel = activeInquiry?.assigned_member?.display_name
    || activeInquiry?.assigned_member?.email
    || 'Ufordelt';

  return (
    <section className="inquiries-expanded-section inquiries-expanded-progression">
      <header>
        <h3>Progresjon</h3>
        {progression?.configured && percent !== null ? <span>{percent}%</span> : null}
      </header>

      {progressionError ? <p className="inquiries-note-error">{progressionError}</p> : null}

      <div className="inquiries-expanded-timeline" aria-label="Kundeløp">
        {steps.map((step, index) => {
          const stepKey = getStepKey(step);
          const isSelected = selectedStepKey === stepKey;
          const stepLogs = progressionLogsByStep.get(stepKey) || [];
          const dateLabel = getProgressionStepDateLabel(step, stepLogs);
          return (
            <button
              key={stepKey || `step-${index}`}
              type="button"
              className={[
                'inquiries-expanded-step-button',
                `is-${getProgressionStepTone(step)}`,
                isSelected ? 'is-selected' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onToggleStep(stepKey)}
              aria-expanded={isSelected}
            >
              <span className="inquiries-expanded-step-marker">{index + 1}</span>
              <span className="inquiries-expanded-step-copy">
                <span className="inquiries-expanded-step-title-row">
                  <strong>{step?.label || step?.name || `Steg ${index + 1}`}</strong>
                  <span className="inquiries-expanded-step-expand" aria-hidden="true">
                    <ExpandRowIcon expanded={isSelected} />
                  </span>
                </span>
                <small>{getProgressionStepStatusLabel(step)}</small>
                {dateLabel ? <small className="inquiries-expanded-step-date">{dateLabel}</small> : null}
              </span>
            </button>
          );
        })}
      </div>

      {selectedStep ? (
        <div className="inquiry-detail-step-panel inquiries-expanded-step-panel">
          <header className="inquiry-detail-step-panel-header">
            <h3>{selectedStep.label}</h3>
            {detailLoading ? <span>Laster detaljer…</span> : null}
          </header>

          {selectedStepType === 'log' ? (
            <>
              <div className="inquiry-detail-log-toolbar">
                <label className="inquiry-detail-checkbox-line">
                  <input
                    type="checkbox"
                    checked={Boolean(selectedStepDraft.approved)}
                    disabled={!canManageInbox || selectedStepSaving}
                    onChange={(event) => onUpdateDraft(getStepKey(selectedStep), { approved: event.target.checked })}
                  />
                  <span>Godkjenn</span>
                </label>
                {canManageInbox ? (
                  <button
                    type="button"
                    className="button button-secondary button-small"
                    disabled={selectedStepSaving}
                    onClick={() => onStartLogDraft(selectedStep)}
                  >
                    Ny føring
                  </button>
                ) : null}
              </div>

              <label className="inquiry-detail-control is-full-width">
                <span>Kommentar</span>
                <textarea
                  className="input inquiries-note-textarea inquiry-detail-casework-textarea"
                  value={selectedStepDraft.comment || ''}
                  disabled={!canManageInbox || selectedStepSaving}
                  onChange={(event) => onUpdateDraft(getStepKey(selectedStep), { comment: event.target.value })}
                  placeholder="Legg til en kommentar for steget"
                />
              </label>

              <div className="inquiry-detail-progression-log-table-wrap">
                <table className="inquiry-detail-progression-log-table">
                  <thead>
                    <tr>
                      {(selectedStep.fields || []).map((field) => (
                        <th key={field.key}>{field.label}</th>
                      ))}
                      <th>Ført av</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStepLogs.length === 0 && !selectedLogDraftOpen ? (
                      <tr>
                        <td colSpan={(selectedStep.fields || []).length + 2} className="inquiry-detail-log-empty">
                          Ingen føringer.
                        </td>
                      </tr>
                    ) : null}
                    {selectedStepLogs.map((entry) => (
                      <tr key={entry.id}>
                        {(selectedStep.fields || []).map((field) => (
                          <td key={`${entry.id}-${field.key}`}>{formatDetailValue(entry.values?.[field.key])}</td>
                        ))}
                        <td>
                          <strong>{entry.created_by?.display_name || entry.created_by?.email || 'System'}</strong>
                          <small>{formatDateTime(entry.created_at)}</small>
                        </td>
                        <td className="inquiry-detail-log-actions">
                          {canManageInbox ? (
                            <>
                              <button
                                type="button"
                                className="button button-secondary button-small"
                                disabled={selectedStepSaving}
                                onClick={() => onStartLogEdit(selectedStep, entry)}
                              >
                                Rediger
                              </button>
                              <button
                                type="button"
                                className="button button-secondary button-small"
                                disabled={progressionSavingKey === `${getStepKey(selectedStep)}:log:${entry.id}`}
                                onClick={() => onDeleteLog(getStepKey(selectedStep), entry.id)}
                              >
                                Slett
                              </button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                    {selectedLogDraftOpen ? (
                      <tr className="inquiry-detail-log-draft-row">
                        {(selectedStep.fields || []).map((field) => {
                          const stepKey = getStepKey(selectedStep);
                          return (
                            <td key={`draft-${field.key}`}>
                              {renderLogDraftInput({
                                field,
                                stepKey,
                                value: selectedLogDraft?.[field.key],
                                disabled: !canManageInbox || selectedStepSaving,
                                onChange: onUpdateLogDraft,
                              })}
                            </td>
                          );
                        })}
                        <td>{selectedLogEditId ? 'Redigerer føring' : 'Ny føring'}</td>
                        <td />
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          ) : selectedStepType === 'checkbox' ? (
            <div className="inquiry-detail-task-state-panel">
              <label className="inquiry-detail-control">
                <span>Status</span>
                <select
                  className="select"
                  value={selectedStepDraft.taskStateKey || 'todo'}
                  disabled={!canManageInbox || selectedStepSaving}
                  onChange={(event) => {
                    const newState = event.target.value;
                    const patch = { taskStateKey: newState };
                    if (newState && newState !== 'todo' && !selectedStepDraft.taskCompletedAt) {
                      patch.taskCompletedAt = new Date().toISOString().slice(0, 16);
                    }
                    onUpdateDraft(getStepKey(selectedStep), patch);
                  }}
                >
                  {getTaskStepStateOptions(selectedStep).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              {getTaskStepStateOptions(selectedStep).find((option) => option.value === (selectedStepDraft.taskStateKey || 'todo'))?.description ? (
                <p>{getTaskStepStateOptions(selectedStep).find((option) => option.value === (selectedStepDraft.taskStateKey || 'todo'))?.description}</p>
              ) : null}
              {selectedStepDraft.taskStateKey && selectedStepDraft.taskStateKey !== 'todo' ? (
                <AppointmentDateTimeRangeField
                  startValue={selectedStepDraft.taskCompletedAt || ''}
                  endValue=""
                  disabled={!canManageInbox || selectedStepSaving}
                  onChange={({ plannedStartAt }) => onUpdateDraft(getStepKey(selectedStep), { taskCompletedAt: plannedStartAt })}
                />
              ) : null}
              <label className="inquiry-detail-control is-full-width">
                <span>Kommentar</span>
                <textarea
                  className="input inquiries-note-textarea inquiry-detail-casework-textarea"
                  value={selectedStepDraft.comment || ''}
                  disabled={!canManageInbox || selectedStepSaving}
                  onChange={(event) => onUpdateDraft(getStepKey(selectedStep), { comment: event.target.value })}
                  placeholder="Legg til en kommentar for steget"
                />
              </label>
            </div>
          ) : (
            <div className="inquiry-detail-step-panel-grid">
              <label className="inquiry-detail-control">
                <span>Ansvarlig</span>
                <select
                  className="select"
                  value={selectedStepDraft.assignedAccountId || ''}
                  disabled={!canManageInbox || selectedStepSaving}
                  onChange={(event) => onUpdateDraft(getStepKey(selectedStep), { assignedAccountId: event.target.value })}
                >
                  <option value="">{activeInquiry?.assigned_to ? assignedMemberLabel : 'Ufordelt'}</option>
                  {assignees
                    .filter((assignee) => assignee.account_id !== activeInquiry?.assigned_to)
                    .map((assignee) => (
                      <option key={assignee.account_id} value={assignee.account_id}>
                        {assignee.display_name || assignee.email}
                      </option>
                    ))}
                </select>
              </label>
              <AppointmentDateTimeRangeField
                startValue={selectedStepDraft.plannedStartAt || ''}
                endValue={selectedStepDraft.plannedEndAt || ''}
                disabled={!canManageInbox || selectedStepSaving}
                onChange={(patch) => onUpdateDraft(getStepKey(selectedStep), patch)}
              />
              <fieldset className="inquiry-detail-attendance-control is-full-width">
                <legend>Oppmøte</legend>
                <div className="inquiry-detail-attendance-toggle">
                  <button
                    type="button"
                    className={selectedStepDraft.attendanceType === 'digital' ? 'is-selected' : ''}
                    disabled={!canManageInbox || selectedStepSaving}
                    onClick={() => onUpdateDraft(getStepKey(selectedStep), { attendanceType: 'digital' })}
                  >
                    Digitalt
                  </button>
                  <button
                    type="button"
                    className={selectedStepDraft.attendanceType === 'physical' ? 'is-selected' : ''}
                    disabled={!canManageInbox || selectedStepSaving}
                    onClick={() => onUpdateDraft(getStepKey(selectedStep), { attendanceType: 'physical' })}
                  >
                    Fysisk
                  </button>
                </div>
              </fieldset>
              {selectedStepDraft.attendanceType === 'digital' ? (
                <>
                  <GoogleMeetSection
                    draft={selectedStepDraft}
                    onUpdateDraft={(patch) => onUpdateDraft(getStepKey(selectedStep), patch)}
                    companyId={companyId}
                    disabled={!canManageInbox}
                    saving={selectedStepSaving}
                    assigneeEmail={
                      selectedStepDraft.assignedAccountId
                        ? (assignees.find((a) => a.account_id === selectedStepDraft.assignedAccountId) || {}).email || ''
                        : activeInquiry?.assigned_member?.email || ''
                    }
                  />
                  {!selectedStepDraft.useGoogleMeet ? (
                    <label className="inquiry-detail-control is-full-width">
                      <span>Lenke</span>
                      <input
                        className="input"
                        type="url"
                        value={selectedStepDraft.meetingUrl || ''}
                        disabled={!canManageInbox || selectedStepSaving}
                        placeholder="https://"
                        onChange={(event) => onUpdateDraft(getStepKey(selectedStep), { meetingUrl: event.target.value })}
                      />
                    </label>
                  ) : null}
                </>
              ) : null}
              {selectedStepDraft.attendanceType === 'physical' ? (
                <label className="inquiry-detail-control is-full-width">
                  <span>Adresse</span>
                  <LocationInput
                    value={selectedStepDraft.locationValue || selectedStepDraft.locationAddress || ''}
                    disabled={!canManageInbox || selectedStepSaving}
                    onChange={(nextValue) => onUpdateDraft(getStepKey(selectedStep), {
                      locationValue: nextValue,
                      locationAddress: getLocationAddress(nextValue),
                    })}
                  />
                </label>
              ) : null}
              <label className="inquiry-detail-control is-full-width">
                <span>Kommentar</span>
                <textarea
                  className="input inquiries-note-textarea inquiry-detail-casework-textarea"
                  value={selectedStepDraft.comment || ''}
                  disabled={!canManageInbox || selectedStepSaving}
                  onChange={(event) => onUpdateDraft(getStepKey(selectedStep), { comment: event.target.value })}
                />
              </label>
            </div>
          )}

          {canManageInbox && selectedStepDirty ? (
            <div className="inquiry-detail-step-save-prompt">
              <span>Ulagrede endringer</span>
              <div>
                <button
                  type="button"
                  className="button button-secondary"
                  disabled={selectedStepSaving}
                  onClick={() => onDiscardChanges(selectedStep)}
                >
                  Forkast
                </button>
                <button
                  type="button"
                  className="button button-primary"
                  disabled={selectedStepSaving || !companyId}
                  onClick={() => onSavePanel(selectedStep)}
                >
                  {selectedStepSaving ? 'Lagrer…' : 'Lagre endringer'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

const InquiryExpandedDetails = ({
  inquiry,
  companyId,
  canManageInbox,
  assignees,
  onInquiryUpdated,
  onOpenInquiry,
}) => {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedStepKey, setSelectedStepKey] = useState(null);
  const [progressionDrafts, setProgressionDrafts] = useState({});
  const [progressionLogDrafts, setProgressionLogDrafts] = useState({});
  const [progressionLogDraftRows, setProgressionLogDraftRows] = useState({});
  const [progressionLogEditIds, setProgressionLogEditIds] = useState({});
  const [progressionSavingKey, setProgressionSavingKey] = useState(null);
  const [progressionError, setProgressionError] = useState(null);
  const activeInquiry = detail?.inquiry || inquiry;
  const progression = activeInquiry?.progression;
  const steps = useMemo(
    () => (Array.isArray(progression?.steps) ? progression.steps : []),
    [progression?.steps],
  );
  const summary = getInquiryDetailSummary(activeInquiry);
  const attributes = getInquiryDetailAttributes(activeInquiry);
  const progressionLogs = Array.isArray(detail?.progression_logs) ? detail.progression_logs : [];
  const hasSummary = Boolean(summary && summary !== '—');
  const hasProgression = Boolean(progression?.configured && steps.length > 0);
  const hasAttributes = attributes.length > 0;

  useEffect(() => {
    const nextDrafts = {};
    const nextLogDrafts = {};
    steps.forEach((step) => {
      const stepKey = getStepKey(step);
      if (!stepKey) return;
      nextDrafts[stepKey] = createProgressionStepDraft(step, activeInquiry?.assigned_to || null);
      if (getProgressionStepType(step) === 'log') {
        nextLogDrafts[stepKey] = createProgressionLogDraft(step);
      }
    });
    setProgressionDrafts(nextDrafts);
    setProgressionLogDrafts(nextLogDrafts);
    setProgressionLogDraftRows({});
    setProgressionLogEditIds({});
    setProgressionError(null);
    setSelectedStepKey((currentKey) => (
      currentKey && steps.some((step) => getStepKey(step) === currentKey) ? currentKey : null
    ));
  }, [activeInquiry?.assigned_to, steps]);

  const loadDetail = useCallback(async () => {
    if (!companyId || !activeInquiry?.id || detail || detailLoading) return;
    setDetailLoading(true);
    setProgressionError(null);
    try {
      const response = await DashboardApi.getCompanyInboxDetail(companyId, activeInquiry.id);
      setDetail(response || null);
      if (response?.inquiry) onInquiryUpdated(response.inquiry);
    } catch (err) {
      const message = err.message || 'Kunne ikke hente kundeløpdetaljer.';
      setProgressionError(message);
      notifyError(message, { title: 'Kunne ikke hente kundeløpdetaljer', source: 'Henvendelser' });
    } finally {
      setDetailLoading(false);
    }
  }, [activeInquiry?.id, companyId, detail, detailLoading, onInquiryUpdated]);

  const applyProgressionResponse = useCallback((response) => {
    if (!response || typeof response !== 'object') return;
    setDetail(response);
    if (response.inquiry) onInquiryUpdated(response.inquiry);
  }, [onInquiryUpdated]);

  const handleToggleStep = (stepKey) => {
    setSelectedStepKey((current) => (current === stepKey ? null : stepKey));
    if (stepKey) {
      loadDetail();
    }
  };

  const updateProgressionDraft = (stepKey, patch) => {
    setProgressionDrafts((prev) => ({
      ...prev,
      [stepKey]: {
        ...(prev[stepKey] || {}),
        ...patch,
      },
    }));
    if (progressionError) setProgressionError(null);
  };

  const updateProgressionLogDraft = (stepKey, fieldKey, value) => {
    setProgressionLogDrafts((prev) => ({
      ...prev,
      [stepKey]: {
        ...(prev[stepKey] || {}),
        [fieldKey]: value,
      },
    }));
    if (progressionError) setProgressionError(null);
  };

  const startProgressionLogDraft = (step) => {
    const stepKey = getStepKey(step);
    if (!stepKey) return;
    setProgressionLogDraftRows((prev) => ({ ...prev, [stepKey]: true }));
    setProgressionLogEditIds((prev) => ({ ...prev, [stepKey]: null }));
    setProgressionLogDrafts((prev) => ({
      ...prev,
      [stepKey]: createProgressionLogDraft(step),
    }));
    if (progressionError) setProgressionError(null);
  };

  const startProgressionLogEdit = (step, entry) => {
    const stepKey = getStepKey(step);
    if (!stepKey || !entry?.id) return;
    setProgressionLogDraftRows((prev) => ({ ...prev, [stepKey]: true }));
    setProgressionLogEditIds((prev) => ({ ...prev, [stepKey]: entry.id }));
    setProgressionLogDrafts((prev) => ({
      ...prev,
      [stepKey]: {
        ...createProgressionLogDraft(step),
        ...(entry.values || {}),
      },
    }));
    if (progressionError) setProgressionError(null);
  };

  const handleSaveProgressionStep = async (step, patch = {}) => {
    const stepKey = getStepKey(step);
    if (!companyId || !activeInquiry?.id || !stepKey) return false;
    const draft = {
      ...(progressionDrafts[stepKey] || {}),
      ...patch,
    };
    const stepType = getProgressionStepType(step);
    const normalizedDraft = normalizeTaskDraft(draft);

    if (
      stepType === 'appointment'
      && draft.meetingProvider
      && !draft.googleImpersonatedEmail
    ) {
      const message = 'Du må verifisere en Google Workspace-e-postadresse før du kan lagre med Google Meet.';
      setProgressionError(message);
      return false;
    }

    if (
      stepType === 'appointment'
      && draft.meetingProvider
      && !draft.plannedStartAt
    ) {
      const message = 'Du må sette en dato for møtet før du kan opprette et videomøte.';
      setProgressionError(message);
      return false;
    }

    if (
      stepType === 'appointment'
      && draft.meetingProvider === 'microsoft'
    ) {
      try {
        const ms = await DashboardApi.getMicrosoftStatus();
        if (!ms?.connected) {
          const message = 'Du må koble til Microsoft Teams før du kan opprette videomøter.';
          setProgressionError(message);
          return false;
        }
      } catch {
        const message = 'Kunne ikke sjekke Microsoft-tilkoblingen. Prøv igjen.';
        setProgressionError(message);
        return false;
      }
    }

    const payload = stepType === 'checkbox'
      ? {
        taskStateKey: draft.taskStateKey === 'todo' ? null : draft.taskStateKey,
        comment: normalizedDraft.comment,
        taskCompletedAt: draft.taskCompletedAt || null,
      }
      : stepType === 'log'
        ? {
          approved: Boolean(draft.approved),
          comment: normalizedDraft.comment,
        }
        : {
          assignedAccountId: normalizedDraft.assignedAccountId || null,
          plannedStartAt: inputDateTimeToIso(normalizedDraft.plannedStartAt),
          plannedEndAt: inputDateTimeToIso(normalizedDraft.plannedEndAt),
          comment: normalizedDraft.comment,
          attendanceType: normalizedDraft.attendanceType || null,
          meetingUrl: normalizedDraft.attendanceType === 'digital' && !draft.meetingProvider ? normalizedDraft.meetingUrl || null : null,
          location: normalizedDraft.attendanceType === 'physical' && (draft.locationValue || normalizedDraft.locationAddress)
            ? (normalizeLocationValue(draft.locationValue) || { address: normalizedDraft.locationAddress })
            : null,
          useGoogleMeet: draft.meetingProvider === 'google',
          useMicrosoftTeams: draft.meetingProvider === 'microsoft',
          googleImpersonatedEmail: draft.meetingProvider === 'google' ? (draft.googleImpersonatedEmail || null) : null,
          googleMeetGuests: draft.meetingProvider ? (Array.isArray(draft.meetingGuests) ? draft.meetingGuests : []) : [],
          googleMeetDescription: draft.meetingProvider ? (draft.meetingDescription || null) : null,
        };
    setProgressionSavingKey(stepKey);
    setProgressionError(null);
    try {
      const response = await DashboardApi.updateCompanyInboxProgression(companyId, activeInquiry.id, stepKey, payload);
      applyProgressionResponse(response);

      if (draft.meetingProvider && step.type === 'appointment') {
        const updatedStep = (response?.inquiry?.progression?.steps || [])
          .find((s) => getStepKey(s) === stepKey);
        if (updatedStep?.metadata) {
          updateProgressionDraft(stepKey, {
            googleMeetUrl: updatedStep.metadata.google_meet_url || '',
            microsoftTeamsUrl: updatedStep.metadata.microsoft_teams_url || '',
            googleEventId: updatedStep.metadata.google_event_id || '',
          });
        }
      }

      notifySuccess('Kundeløp ble oppdatert.', { source: 'Henvendelser' });
      if (response?._googleSyncError) {
        notifyError(response._googleSyncError, { title: 'Google Meet-synkronisering feilet', source: 'Henvendelser' });
      }
      return true;
    } catch (err) {
      const message = err.message || 'Kunne ikke oppdatere kundeløp.';
      setProgressionError(message);
      notifyError(message, { title: 'Kunne ikke oppdatere kundeløp', source: 'Henvendelser' });
      return false;
    } finally {
      setProgressionSavingKey(null);
    }
  };

  const handleSaveProgressionLog = async (step) => {
    const stepKey = getStepKey(step);
    if (!companyId || !activeInquiry?.id || !stepKey) return false;
    const editId = progressionLogEditIds[stepKey] || null;
    setProgressionSavingKey(`${stepKey}:log`);
    setProgressionError(null);
    try {
      const payload = { values: progressionLogDrafts[stepKey] || {} };
      const response = editId
        ? await DashboardApi.updateCompanyInboxProgressionLog(companyId, activeInquiry.id, stepKey, editId, payload)
        : await DashboardApi.createCompanyInboxProgressionLog(companyId, activeInquiry.id, stepKey, payload);
      applyProgressionResponse(response);
      setProgressionLogDraftRows((prev) => ({ ...prev, [stepKey]: false }));
      setProgressionLogEditIds((prev) => ({ ...prev, [stepKey]: null }));
      notifySuccess(editId ? 'Loggen ble oppdatert.' : 'Loggen ble lagt til.', { source: 'Henvendelser' });
      return true;
    } catch (err) {
      const message = err.message || 'Kunne ikke lagre logg.';
      setProgressionError(message);
      notifyError(message, { title: 'Kunne ikke lagre logg', source: 'Henvendelser' });
      return false;
    } finally {
      setProgressionSavingKey(null);
    }
  };

  const handleDiscardProgressionChanges = (step) => {
    const stepKey = getStepKey(step);
    if (!stepKey) return;
    if (getProgressionStepType(step) === 'log') {
      setProgressionDrafts((prev) => ({
        ...prev,
        [stepKey]: createProgressionStepDraft(step, activeInquiry?.assigned_to || null),
      }));
      setProgressionLogDrafts((prev) => ({
        ...prev,
        [stepKey]: createProgressionLogDraft(step),
      }));
      setProgressionLogDraftRows((prev) => ({ ...prev, [stepKey]: false }));
      setProgressionLogEditIds((prev) => ({ ...prev, [stepKey]: null }));
    } else {
      setProgressionDrafts((prev) => ({
        ...prev,
        [stepKey]: createProgressionStepDraft(step, activeInquiry?.assigned_to || null),
      }));
    }
    setProgressionError(null);
  };

  const handleSaveProgressionPanel = async (step) => {
    if (!step) return;
    if (getProgressionStepType(step) === 'log') {
      const stepKey = getStepKey(step);
      const approvalDirty = isStepDraftDirty(step, progressionDrafts[stepKey] || {}, activeInquiry?.assigned_to || null);
      const logDirty = progressionLogDraftRows[stepKey]
        || Boolean(progressionLogEditIds[stepKey])
        || isLogDraftDirty(progressionLogDrafts[stepKey], step.fields);
      if (logDirty) {
        const logSaved = await handleSaveProgressionLog(step);
        if (!logSaved) return;
      }
      if (approvalDirty) {
        await handleSaveProgressionStep(step);
      }
      return;
    }
    await handleSaveProgressionStep(step);
  };

  const handleDeleteProgressionLog = async (stepKey, logId) => {
    if (!companyId || !activeInquiry?.id || !stepKey || !logId) return;
    setProgressionSavingKey(`${stepKey}:log:${logId}`);
    setProgressionError(null);
    try {
      const response = await DashboardApi.deleteCompanyInboxProgressionLog(companyId, activeInquiry.id, stepKey, logId);
      applyProgressionResponse(response);
      notifySuccess('Loggen ble slettet.', { source: 'Henvendelser' });
    } catch (err) {
      const message = err.message || 'Kunne ikke slette logg.';
      setProgressionError(message);
      notifyError(message, { title: 'Kunne ikke slette logg', source: 'Henvendelser' });
    } finally {
      setProgressionSavingKey(null);
    }
  };

  if (!hasSummary && !hasProgression && !hasAttributes) {
    return null;
  }

  return (
    <div className={[
      'inquiries-expanded-card',
      hasProgression ? 'has-progression' : '',
      hasSummary ? 'has-summary' : '',
      hasAttributes ? 'has-attributes' : '',
    ].filter(Boolean).join(' ')}
    >
      {hasProgression ? (
        <InquiryProgressionTimeline
          activeInquiry={activeInquiry}
          companyId={companyId}
          canManageInbox={canManageInbox}
          assignees={assignees}
          progressionLogs={progressionLogs}
          selectedStepKey={selectedStepKey}
          progressionDrafts={progressionDrafts}
          progressionLogDrafts={progressionLogDrafts}
          progressionLogDraftRows={progressionLogDraftRows}
          progressionLogEditIds={progressionLogEditIds}
          progressionSavingKey={progressionSavingKey}
          progressionError={progressionError}
          detailLoading={detailLoading}
          onToggleStep={handleToggleStep}
          onUpdateDraft={updateProgressionDraft}
          onUpdateLogDraft={updateProgressionLogDraft}
          onStartLogDraft={startProgressionLogDraft}
          onStartLogEdit={startProgressionLogEdit}
          onDeleteLog={handleDeleteProgressionLog}
          onDiscardChanges={handleDiscardProgressionChanges}
          onSavePanel={handleSaveProgressionPanel}
        />
      ) : null}

      {hasSummary ? (
        <section className="inquiries-expanded-section inquiries-expanded-summary">
        <h3>Oppsummering</h3>
        <p>{summary}</p>
        </section>
      ) : null}

      {hasAttributes ? (
        <section className="inquiries-expanded-section inquiries-expanded-attribute-section">
        <h3>Attributter</h3>
        <dl className="inquiries-expanded-attributes">
          {attributes.map((attribute) => (
            <div key={attribute.key}>
              <dt>{attribute.label}</dt>
              <dd>{attribute.value}</dd>
            </div>
          ))}
        </dl>
        </section>
      ) : null}
      <div className="inquiries-expanded-actions">
        <button
          type="button"
          className="button button-secondary button-small"
          onClick={() => onOpenInquiry(activeInquiry.id)}
        >
          Åpne henvendelse
        </button>
      </div>
    </div>
  );
};

const getInitialInboxAssigneeFilter = (searchParams) => {
  const value = searchParams.get('assignedAccountId') || searchParams.get('assignedTo') || '';
  return value.trim() || 'alle';
};

const getInitialInboxProductFilter = (searchParams) => {
  const value = searchParams.get('botProductId') || searchParams.get('bot_product_id') || '';
  return value.trim();
};

const InquiriesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedCompanyId, selectedCompany, permissions } = useAuth();
  const { on: onRealtimeEvent, socketReady } = useDashboardRealtime();
  const companyId = selectedCompanyId || selectedCompany?.id || null;
  const canView = Boolean(permissions?.canViewInbox || permissions?.canViewInnsikt);
  const canManageInbox = Boolean(permissions?.canManageInbox || permissions?.canEditBots);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [inquiriesTotal, setInquiriesTotal] = useState(0);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [, setError] = useState(null);

  const [stageFilter, setStageFilter] = useState('alle');
  const [sourceFilter, setSourceFilter] = useState('alle');
  const [assigneeFilter, setAssigneeFilter] = useState(() => getInitialInboxAssigneeFilter(searchParams));
  const [botProductFilter, setBotProductFilter] = useState(() => getInitialInboxProductFilter(searchParams));
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState('desc');
  const [columnFilters, setColumnFilters] = useState({});
  const [activeColumnFilter, setActiveColumnFilter] = useState(null);
  const [columnFilterDraft, setColumnFilterDraft] = useState('');

  const [columnDraft, setColumnDraft] = useState([]);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [columnConfigSaving, setColumnConfigSaving] = useState(false);
  const [columnConfigError, setColumnConfigError] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [inlineUpdateKey, setInlineUpdateKey] = useState(null);
  const [, setUpdateError] = useState(null);
  const [expandedInquiryIds, setExpandedInquiryIds] = useState(() => new Set());
  const [recentInquiryIds, setRecentInquiryIds] = useState(() => new Set());
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualDraft, setManualDraft] = useState(() => createManualInquiryDraft());
  const [manualInquiryTypes, setManualInquiryTypes] = useState([]);
  const [manualProducts, setManualProducts] = useState([]);
  const [manualResolvedAttributes, setManualResolvedAttributes] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualError, setManualError] = useState(null);
  const columnReorderRowRefs = useRef(new Map());
  const tableShellRef = useRef(null);
  const inquiriesRef = useRef([]);
  const recentInquiryTimersRef = useRef(new Map());
  const [expandedViewportWidth, setExpandedViewportWidth] = useState(null);
  const [isMobileView, setIsMobileView] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const listener = (event) => {
      setIsMobileView(event.matches);
      if (!event.matches) {
        setIsFilterModalOpen(false);
      }
    };
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    inquiriesRef.current = inquiries;
  }, [inquiries]);

  useEffect(() => () => {
    recentInquiryTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    recentInquiryTimersRef.current.clear();
  }, []);

  const markRecentInquiries = useCallback((ids = []) => {
    const normalizedIds = ids
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);
    if (normalizedIds.length === 0) return;

    setRecentInquiryIds((current) => {
      const next = new Set(current);
      normalizedIds.forEach((id) => next.add(id));
      return next;
    });

    normalizedIds.forEach((id) => {
      const existingTimer = recentInquiryTimersRef.current.get(id);
      if (existingTimer) {
        window.clearTimeout(existingTimer);
      }
      const timer = window.setTimeout(() => {
        recentInquiryTimersRef.current.delete(id);
        setRecentInquiryIds((current) => {
          if (!current.has(id)) return current;
          const next = new Set(current);
          next.delete(id);
          return next;
        });
      }, 5500);
      recentInquiryTimersRef.current.set(id, timer);
    });
  }, []);

  const loadSummary = useCallback(async ({ silent = false } = {}) => {
    if (!companyId) {
      setSummary(null);
      return;
    }

    if (!silent) {
      setSummaryLoading(true);
    }
    setError(null);
    try {
      const data = await DashboardApi.getCompanyInboxSummary(companyId);
      setSummary(data || null);
    } catch (err) {
      setSummary(null);
      const message = err.message || 'Kunne ikke hente oversikt over inbox.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke hente henvendelser', source: 'Henvendelser' });
    } finally {
      if (!silent) {
        setSummaryLoading(false);
      }
    }
  }, [companyId]);

  const buildInboxParams = useCallback((offset = page * PAGE_SIZE) => {
    const params = {
      limit: PAGE_SIZE,
      offset,
    };

    if (stageFilter !== 'alle') params.stage = stageFilter;
    if (sourceFilter !== 'alle') params.source = sourceFilter;
    if (assigneeFilter !== 'alle') params.assignedAccountId = assigneeFilter;
    if (botProductFilter) params.botProductId = botProductFilter;
    if (unreadOnly) params.unreadOnly = '1';
    if (searchQuery) params.q = searchQuery;
    if (sortBy) {
      params.sortBy = sortBy;
      params.sortDirection = sortDirection;
    }
    if (Object.keys(columnFilters).length > 0) {
      params.columnFilters = JSON.stringify(columnFilters);
    }

    return params;
  }, [assigneeFilter, botProductFilter, columnFilters, page, searchQuery, sortBy, sortDirection, sourceFilter, stageFilter, unreadOnly]);

  const loadInquiries = useCallback(async () => {
    if (!companyId) {
      setInquiries([]);
      setInquiriesTotal(0);
      return;
    }

    setInquiryLoading(true);
    setError(null);
    try {
      const data = await DashboardApi.getCompanyInbox(companyId, buildInboxParams());
      setInquiries(Array.isArray(data?.inquiries) ? data.inquiries : []);
      setInquiriesTotal(Number(data?.total || 0));
    } catch (err) {
      setInquiries([]);
      setInquiriesTotal(0);
      const message = err.message || 'Kunne ikke hente inbox.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke hente inbox', source: 'Henvendelser' });
    } finally {
      setInquiryLoading(false);
    }
  }, [buildInboxParams, companyId]);

  const refreshInquiriesInBackground = useCallback(async () => {
    if (!companyId || !canView) return;
    try {
      const [summaryResult, inboxResult] = await Promise.allSettled([
        DashboardApi.getCompanyInboxSummary(companyId),
        DashboardApi.getCompanyInbox(companyId, buildInboxParams(0)),
      ]);

      if (summaryResult.status === 'fulfilled') {
        setSummary(summaryResult.value || null);
      }

      if (inboxResult.status !== 'fulfilled') return;
      const nextRows = Array.isArray(inboxResult.value?.inquiries) ? inboxResult.value.inquiries : [];
      setInquiriesTotal(Number(inboxResult.value?.total || 0));

      if (page !== 0 || nextRows.length === 0) return;

      const previousRows = inquiriesRef.current;
      const previousIds = new Set(previousRows.map((inquiry) => Number(inquiry.id)).filter(Boolean));
      const nextRowsById = new Map(nextRows.map((inquiry) => [Number(inquiry.id), inquiry]));
      const incomingRows = nextRows.filter((inquiry) => {
        const id = Number(inquiry.id);
        return id && !previousIds.has(id);
      });

      if (incomingRows.length > 0) {
        markRecentInquiries(incomingRows.map((inquiry) => inquiry.id));
      }

      setInquiries((currentRows) => {
        const currentIds = new Set(currentRows.map((inquiry) => Number(inquiry.id)).filter(Boolean));
        const currentIncomingRows = nextRows.filter((inquiry) => {
          const id = Number(inquiry.id);
          return id && !currentIds.has(id);
        });
        const updatedCurrentRows = currentRows.map((inquiry) => nextRowsById.get(Number(inquiry.id)) || inquiry);
        return currentIncomingRows.length > 0
          ? [...currentIncomingRows, ...updatedCurrentRows]
          : updatedCurrentRows;
      });
    } catch (_error) {
      // Live background refresh should never interrupt the active inbox workflow.
    }
  }, [buildInboxParams, canView, companyId, markRecentInquiries, page]);

  useEffect(() => {
    setPage(0);
  }, [companyId]);

  useEffect(() => {
    setAssigneeFilter(getInitialInboxAssigneeFilter(searchParams));
    setBotProductFilter(getInitialInboxProductFilter(searchParams));
    setPage(0);
  }, [companyId, searchParams]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  useEffect(() => {
    if (!canView || !companyId || !socketReady) return undefined;
    return onRealtimeEvent('inquiry_created', () => {
      refreshInquiriesInBackground();
    });
  }, [canView, companyId, onRealtimeEvent, refreshInquiriesInBackground, socketReady]);

  useEffect(() => {
    if (!editingCell) return undefined;

    const handlePointerDown = (event) => {
      if (event.target.closest?.('.inquiries-inline-menu-anchor, .inquiries-floating-menu')) {
        return;
      }
      setEditingCell(null);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setEditingCell(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [editingCell]);

  useEffect(() => {
    if (!activeColumnFilter) return undefined;

    const handlePointerDown = (event) => {
      if (event.target.closest?.('.inquiries-column-filter-button, .inquiries-floating-menu')) {
        return;
      }
      setActiveColumnFilter(null);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setActiveColumnFilter(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [activeColumnFilter]);

  useEffect(() => {
    if (!manualModalOpen) {
      setManualInquiryTypes([]);
      return undefined;
    }

    let cancelled = false;
    setManualLoading(true);
    setManualError(null);
    DashboardApi.getCompanyInquiryTypes(companyId)
      .then((response) => {
        if (cancelled) return;
        setManualInquiryTypes(Array.isArray(response?.items) ? response.items : []);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err.message || 'Kunne ikke hente henvendelsestyper.';
        setManualInquiryTypes([]);
        setManualError(message);
      })
      .finally(() => {
        if (!cancelled) {
          setManualLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [companyId, manualModalOpen]);

  useEffect(() => {
    if (!manualModalOpen) {
      setManualProducts([]);
      setManualResolvedAttributes([]);
      return undefined;
    }

    if (!manualDraft.inquiryTypeId) {
      setManualProducts([]);
      setManualResolvedAttributes([]);
      return undefined;
    }

    let cancelled = false;
    setManualLoading(true);
    setManualError(null);
    const params = manualDraft.botProductId ? { productId: manualDraft.botProductId } : {};
    DashboardApi.resolveCompanyUnlinkedInquiryType(companyId, manualDraft.inquiryTypeId, params)
      .then((response) => {
        if (cancelled) return;
        setManualProducts(Array.isArray(response?.available_products) ? response.available_products : []);
        setManualResolvedAttributes(Array.isArray(response?.resolved_attributes) ? response.resolved_attributes : []);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err.message || 'Kunne ikke løse henvendelsestypen.';
        setManualProducts([]);
        setManualResolvedAttributes([]);
        setManualError(message);
      })
      .finally(() => {
        if (!cancelled) {
          setManualLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [companyId, manualDraft.botProductId, manualDraft.inquiryTypeId, manualModalOpen]);

  useEffect(() => {
    if (!manualModalOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && !manualSaving) {
        setManualModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [manualModalOpen, manualSaving]);

  const availableColumns = useMemo(
    () => normalizeColumnList(summary?.available_columns),
    [summary?.available_columns],
  );
  const resolvedVisibleColumns = useMemo(
    () => normalizeColumnList(summary?.visible_columns),
    [summary?.visible_columns],
  );
  const inquirySources = useMemo(
    () => (Array.isArray(summary?.sources) ? summary.sources : []),
    [summary?.sources],
  );
  const summaryAssignees = useMemo(
    () => (Array.isArray(summary?.assignees) ? summary.assignees : []),
    [summary?.assignees],
  );
  const summaryProducts = useMemo(
    () => (Array.isArray(summary?.products) ? summary.products : []),
    [summary?.products],
  );
  const summaryProgressionStages = useMemo(
    () => normalizeProgressionStageFilterOptions(summary?.status_options?.progression_stages),
    [summary?.status_options?.progression_stages],
  );
  const manualProductAttribute = useMemo(
    () => manualResolvedAttributes.find((field) => field?.type === 'product') || null,
    [manualResolvedAttributes],
  );
  const manualVisibleAttributes = useMemo(
    () => manualResolvedAttributes.filter((field) => field?.key && field.key !== manualProductAttribute?.key),
    [manualProductAttribute?.key, manualResolvedAttributes],
  );

  useEffect(() => {
    const availableIds = availableColumns.map((column) => column.id);
    const nextVisible = orderColumnIds(
      resolvedVisibleColumns.length > 0
        ? resolvedVisibleColumns.map((column) => column.id)
        : availableIds,
      new Map(availableColumns.map((column) => [column.id, column])),
      availableIds,
      { ensureSummary: true, useDefaultRank: false },
    );
    setColumnDraft(nextVisible);
  }, [availableColumns, resolvedVisibleColumns]);

  const availableColumnIds = availableColumns.map((column) => column.id);

  const tableColumns = useMemo(() => {
    if (!availableColumns.length) return [];
    const columnsById = new Map(availableColumns.map((column) => [column.id, column]));
    const selectedIds = orderColumnIds(
      columnDraft.length > 0 ? columnDraft : availableColumns.map((column) => column.id),
      columnsById,
      availableColumns.map((column) => column.id),
      { useDefaultRank: false },
    );
    return selectedIds.map((columnId) => columnsById.get(columnId)).filter(Boolean);
  }, [availableColumns, columnDraft]);

  const selectedColumnIds = columnDraft.length > 0 ? columnDraft : availableColumnIds;
  const selectedColumnIdSet = useMemo(() => new Set(selectedColumnIds), [selectedColumnIds]);
  const hiddenRearrangeColumns = useMemo(
    () => availableColumns.filter((column) => !selectedColumnIdSet.has(column.id)),
    [availableColumns, selectedColumnIdSet],
  );

  const displayedInquiries = useMemo(() => {
    const columnsById = new Map(tableColumns.map((column) => [column.id, column]));
    const filteredRows = inquiries.filter((inquiry) => (
      Object.entries(columnFilters).every(([columnId, filter]) => (
        rowMatchesColumnFilter(inquiry, columnsById.get(columnId), filter)
      ))
    ));
    if (!sortBy) return filteredRows;
    const sortColumn = columnsById.get(sortBy);
    if (!sortColumn) return filteredRows;
    return [...filteredRows].sort((left, right) => compareInquiriesByColumn(left, right, sortColumn, sortDirection));
  }, [columnFilters, inquiries, sortBy, sortDirection, tableColumns]);

  useLayoutEffect(() => {
    const element = tableShellRef.current;
    if (!element) return undefined;
    const updateWidth = () => {
      const nextWidth = Math.floor(element.clientWidth || 0);
      setExpandedViewportWidth((current) => (current === nextWidth ? current : nextWidth));
    };
    updateWidth();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(element);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [displayedInquiries.length, expandedInquiryIds.size, tableColumns.length]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(0);
    setSearchQuery(searchInput.trim());
  };

  const handleSortColumn = (columnId) => {
    if (!columnId) return;
    setPage(0);
    if (sortBy !== columnId) {
      setSortBy(columnId);
      setSortDirection('asc');
      return;
    }
    setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
  };

  const handleOpenColumnFilter = (event, columnId) => {
    event.preventDefault();
    event.stopPropagation();
    const existingValue = columnFilters[columnId]?.value || '';
    setColumnFilterDraft(String(existingValue));
    setActiveColumnFilter((current) => (current === columnId ? null : columnId));
  };

  const applyColumnFilter = (columnId, value, mode = 'contains') => {
    const normalizedValue = String(value || '').trim();
    setPage(0);
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (!normalizedValue) {
        delete next[columnId];
      } else {
        next[columnId] = { value: normalizedValue, mode };
      }
      return next;
    });
    setActiveColumnFilter(null);
  };

  const clearColumnFilter = (columnId) => {
    setPage(0);
    setColumnFilterDraft('');
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[columnId];
      return next;
    });
    setActiveColumnFilter(null);
  };

  const refreshInboxData = useCallback(async () => {
    await Promise.all([loadSummary(), loadInquiries()]);
  }, [loadInquiries, loadSummary]);

  const openManualInquiryModal = () => {
    if (!canManageInbox) return;
    setManualDraft(createManualInquiryDraft());
    setManualInquiryTypes([]);
    setManualProducts([]);
    setManualResolvedAttributes([]);
    setManualError(null);
    setManualModalOpen(true);
  };

  const closeManualInquiryModal = () => {
    if (manualSaving) return;
    setManualModalOpen(false);
  };

  const updateManualDraftField = (field, value) => {
    setManualDraft((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'inquiryTypeId'
        ? { botProductId: '', attributeValues: {} }
        : {}),
    }));
    if (manualError) {
      setManualError(null);
    }
  };

  const updateManualAttribute = (field, value) => {
    setManualDraft((prev) => ({
      ...prev,
      attributeValues: {
        ...prev.attributeValues,
        [field.key]: field.type === 'boolean' ? Boolean(value) : value,
      },
    }));
    if (manualError) {
      setManualError(null);
    }
  };

  const handleManualProductChange = (value) => {
    const selectedProduct = manualProducts.find((product) => String(product.id) === String(value));
    setManualDraft((prev) => {
      const nextAttributeValues = { ...prev.attributeValues };
      if (manualProductAttribute?.key) {
        if (!value) {
          delete nextAttributeValues[manualProductAttribute.key];
        } else {
          nextAttributeValues[manualProductAttribute.key] = manualProductAttribute.sourceConfig?.value_mode === 'product_name'
            ? selectedProduct?.name || ''
            : value;
        }
      }
      return {
        ...prev,
        botProductId: value,
        attributeValues: nextAttributeValues,
      };
    });
    if (manualError) {
      setManualError(null);
    }
  };

  const handleSubmitManualInquiry = async (event) => {
    event.preventDefault();
    if (!companyId || manualSaving) return;

    const payload = {
      botId: null,
      inquiryTypeId: manualDraft.inquiryTypeId ? Number(manualDraft.inquiryTypeId) : null,
      botProductId: manualDraft.botProductId ? Number(manualDraft.botProductId) : null,
      assignedAccountId: manualDraft.assignedAccountId || null,
      stage: manualDraft.stage,
      subject: manualDraft.subject,
      attributeValues: manualDraft.attributeValues,
    };

    setManualSaving(true);
    setManualError(null);
    try {
      const response = await DashboardApi.createCompanyInbox(companyId, payload);
      const nextInquiryId = response?.inquiry?.id;
      setManualModalOpen(false);
      setManualDraft(createManualInquiryDraft());
      await refreshInboxData();
      notifySuccess('Henvendelsen ble opprettet.', { source: 'Henvendelser' });
      if (nextInquiryId) {
        navigate(`/henvendelser/${nextInquiryId}`);
      }
    } catch (err) {
      const message = err.message || 'Kunne ikke opprette henvendelsen.';
      setManualError(message);
      notifyError(message, { title: 'Kunne ikke opprette henvendelse', source: 'Henvendelser' });
    } finally {
      setManualSaving(false);
    }
  };

  const handleOpenInquiry = (inquiryId) => {
    if (!inquiryId) return;
    navigate(`/henvendelser/${inquiryId}`);
  };

  const toggleInquiryExpansion = (event, inquiryId) => {
    event.preventDefault();
    event.stopPropagation();
    if (!inquiryId) return;
    setExpandedInquiryIds((prev) => {
      const next = new Set(prev);
      if (next.has(inquiryId)) {
        next.delete(inquiryId);
      } else {
        next.add(inquiryId);
      }
      return next;
    });
  };

  const handleClearQuickFilters = () => {
    setStageFilter('alle');
    setAssigneeFilter('alle');
    setBotProductFilter('');
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next.product;
      return next;
    });
    setPage(0);
  };

  const handleNewQuickFilter = () => {
    setStageFilter('new');
    setPage(0);
  };

  const handleMineQuickFilter = () => {
    setAssigneeFilter('me');
    setPage(0);
  };

  const handleProductQuickFilter = (product) => {
    const productId = Number(product?.product_id ?? product?.id ?? 0) || null;
    const productName = String(product?.name || product?.product_name || '').trim();
    if (productId) {
      setBotProductFilter(String(productId));
      setColumnFilters((prev) => {
        const next = { ...prev };
        delete next.product;
        return next;
      });
    } else {
      setBotProductFilter('');
      setColumnFilters((prev) => (
        productName
          ? { ...prev, product: { value: productName, mode: 'equals' } }
          : prev
      ));
    }
    setPage(0);
  };

  const isEditingCell = (inquiryId, columnId) => (
    editingCell?.inquiryId === inquiryId && editingCell?.columnId === columnId
  );

  const handleStartInlineEdit = (event, inquiryId, columnId) => {
    if (!canManageInbox) return;
    event.preventDefault();
    event.stopPropagation();
    setUpdateError(null);
    setEditingCell({ inquiryId, columnId });
  };

  const handleInlineUpdate = async (inquiryId, columnId, payload) => {
    if (!companyId || !inquiryId) return;

    const updateKey = `${inquiryId}:${columnId}`;
    setInlineUpdateKey(updateKey);
    setUpdateError(null);

    try {
      await DashboardApi.updateCompanyInbox(companyId, inquiryId, payload);
      setEditingCell(null);
      await refreshInboxData();
      notifySuccess('Henvendelsen ble oppdatert.', { source: 'Henvendelser' });
    } catch (err) {
      const message = err.message || 'Kunne ikke oppdatere inbox.';
      setUpdateError(message);
      notifyError(message, { title: 'Kunne ikke oppdatere henvendelse', source: 'Henvendelser' });
    } finally {
      setInlineUpdateKey(null);
    }
  };

  const toggleColumnDraft = (columnId) => {
    if (FIXED_LEADING_COLUMN_IDS.includes(columnId)) return;
    setColumnDraft((prev) => {
      const current = prev.length > 0 ? prev : availableColumnIds;
      if (current.includes(columnId)) {
        const next = current.filter((entry) => entry !== columnId);
        return next.length > 0 ? next : [];
      }
      return orderColumnIds(
        [...current, columnId],
        new Map(availableColumns.map((column) => [column.id, column])),
        availableColumnIds,
        { useDefaultRank: false },
      );
    });
  };

  const animateColumnReorder = (previousRects) => {
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      columnReorderRowRefs.current.forEach((node, columnId) => {
        const previousRect = previousRects.get(columnId);
        if (!node || !previousRect || typeof node.animate !== 'function') return;
        const nextRect = node.getBoundingClientRect();
        const deltaY = previousRect.top - nextRect.top;
        if (Math.abs(deltaY) < 1) return;
        node.animate(
          [
            { transform: `translateY(${deltaY}px)` },
            { transform: 'translateY(0)' },
          ],
          {
            duration: 150,
            easing: 'cubic-bezier(0.2, 0, 0, 1)',
          },
        );
      });
    });
  };

  const moveColumnDraft = (sourceColumnId, targetColumnId) => {
    if (
      !sourceColumnId
      || !targetColumnId
      || sourceColumnId === targetColumnId
      || FIXED_LEADING_COLUMN_IDS.includes(sourceColumnId)
      || FIXED_LEADING_COLUMN_IDS.includes(targetColumnId)
    ) {
      return;
    }

    const previousRects = new Map();
    columnReorderRowRefs.current.forEach((node, columnId) => {
      if (node) previousRects.set(columnId, node.getBoundingClientRect());
    });

    setColumnDraft((prev) => {
      const current = prev.length > 0 ? prev : availableColumnIds;
      const fromIndex = current.indexOf(sourceColumnId);
      const toIndex = current.indexOf(targetColumnId);
      if (fromIndex < 0 || toIndex < 0) return current;

      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return orderColumnIds(
        next,
        new Map(availableColumns.map((column) => [column.id, column])),
        availableColumnIds,
        { useDefaultRank: false },
      );
    });
    animateColumnReorder(previousRects);
  };

  const handleColumnDragStart = (event, columnId) => {
    if (FIXED_LEADING_COLUMN_IDS.includes(columnId)) return;
    setDraggedColumnId(columnId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', columnId);
  };

  const handleColumnDragOver = (event, targetColumnId) => {
    if (FIXED_LEADING_COLUMN_IDS.includes(targetColumnId)) return;
    event.preventDefault();
    const sourceColumnId = draggedColumnId || event.dataTransfer.getData('text/plain');
    moveColumnDraft(sourceColumnId, targetColumnId);
  };

  const handleColumnDrop = (event) => {
    event.preventDefault();
    setDraggedColumnId(null);
  };

  const handleSaveColumns = async () => {
    if (!companyId) return;
    setColumnConfigSaving(true);
    setColumnConfigError(null);

    try {
      const fixedVisibleColumns = FIXED_LEADING_COLUMN_IDS.filter((columnId) => availableColumnIds.includes(columnId));
      const visibleColumns = columnDraft.length > 0
        ? [
          ...fixedVisibleColumns,
          ...columnDraft.filter((columnId) => !fixedVisibleColumns.includes(columnId)),
        ]
        : [];
      const hiddenColumns = visibleColumns.length > 0
        ? availableColumnIds.filter((columnId) => !fixedVisibleColumns.includes(columnId) && !visibleColumns.includes(columnId))
        : [];
      const response = await DashboardApi.updateCompanyInboxColumns(companyId, {
        visibleColumns,
        hiddenColumns,
        columnOrder: visibleColumns.length > 0 ? visibleColumns : [],
      });
      setSummary((prev) => (prev ? { ...prev, ...response } : {
        totals: EMPTY_TOTALS,
        sources: [],
        products: [],
        assignees: [],
        ...response,
      }));
      setColumnConfigOpen(false);
      notifySuccess('Kolonneoppsettet ble lagret.', { source: 'Henvendelser' });
    } catch (err) {
      const message = err.message || 'Kunne ikke lagre kolonneoppsettet.';
      setColumnConfigError(message);
      notifyError(message, { title: 'Kunne ikke lagre kolonner', source: 'Henvendelser' });
    } finally {
      setColumnConfigSaving(false);
    }
  };

  const handleResetColumns = async () => {
    if (!companyId) return;
    setColumnConfigSaving(true);
    setColumnConfigError(null);

    try {
      const response = await DashboardApi.updateCompanyInboxColumns(companyId, {
        visibleColumns: [],
        hiddenColumns: [],
        columnOrder: [],
      });
      setSummary((prev) => (prev ? { ...prev, ...response } : {
        totals: EMPTY_TOTALS,
        sources: [],
        products: [],
        assignees: [],
        ...response,
      }));
      setColumnConfigOpen(false);
      notifySuccess('Kolonneoppsettet ble tilbakestilt.', { source: 'Henvendelser' });
    } catch (err) {
      const message = err.message || 'Kunne ikke tilbakestille kolonneoppsettet.';
      setColumnConfigError(message);
      notifyError(message, { title: 'Kunne ikke tilbakestille kolonner', source: 'Henvendelser' });
    } finally {
      setColumnConfigSaving(false);
    }
  };

  const inquiryTotals = summary?.totals || EMPTY_TOTALS;
  const totalPages = Math.ceil(inquiriesTotal / PAGE_SIZE);
  const manualModal = manualModalOpen && typeof document !== 'undefined'
    ? createPortal(
      <div
        className="modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Ny henvendelse"
        onClick={closeManualInquiryModal}
      >
        <form
          className="modal modal--lg inquiries-create-modal"
          onSubmit={handleSubmitManualInquiry}
          onClick={(event) => event.stopPropagation()}
        >
          <header className="modal-header">
            <div className="modal-title">
              <span className="modal-title-icon">+</span>
              <div>
                <h2>Ny henvendelse</h2>
                <p className="card-subtitle">Registrer en manuell henvendelse i inbox.</p>
              </div>
            </div>
            <button
              type="button"
              className="modal-close"
              onClick={closeManualInquiryModal}
              disabled={manualSaving}
              aria-label="Lukk"
            >
              ×
            </button>
          </header>

          <div className="modal-body inquiries-create-modal-body">
            {manualError ? <p className="inquiries-note-error">{manualError}</p> : null}

            <div className="inquiries-create-grid">
              <label className="inquiry-detail-control">
                <span>Henvendelsestype (valgfritt)</span>
                <select
                  className="select"
                  value={manualDraft.inquiryTypeId}
                  onChange={(event) => updateManualDraftField('inquiryTypeId', event.target.value)}
                  disabled={manualSaving || manualLoading}
                >
                  <option value="">Ingen type</option>
                  {manualInquiryTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name || `Type #${item.id}`}
                    </option>
                  ))}
                </select>
              </label>

              {manualProductAttribute || manualProducts.length > 0 ? (
                <label className="inquiry-detail-control">
                  <span>Produkt</span>
                  <select
                    className="select"
                    value={manualDraft.botProductId}
                    onChange={(event) => handleManualProductChange(event.target.value)}
                    disabled={manualSaving || manualLoading || !manualDraft.inquiryTypeId}
                  >
                    <option value="">Uten produkt</option>
                    {manualProducts.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <label className="inquiry-detail-control">
                <span>Ansvarlig</span>
                <select
                  className="select"
                  value={manualDraft.assignedAccountId}
                  onChange={(event) => updateManualDraftField('assignedAccountId', event.target.value)}
                  disabled={manualSaving}
                >
                  <option value="">Ufordelt</option>
                  {summaryAssignees.map((assignee) => (
                    <option key={assignee.account_id} value={assignee.account_id}>
                      {assignee.display_name || assignee.email}
                    </option>
                  ))}
                </select>
              </label>

              <label className="inquiry-detail-control">
                <span>Status</span>
                <select
                  className="select"
                  value={manualDraft.stage}
                  onChange={(event) => updateManualDraftField('stage', event.target.value)}
                  disabled={manualSaving}
                >
                  {INLINE_STAGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <label className="inquiry-detail-control is-full-width">
                <span>Emne</span>
                <input
                  className="input"
                  value={manualDraft.subject}
                  onChange={(event) => updateManualDraftField('subject', event.target.value)}
                  disabled={manualSaving}
                />
              </label>
            </div>

            {manualVisibleAttributes.length > 0 ? (
              <section className="inquiries-create-attributes">
                <h3>Attributter</h3>
                <div className="inquiries-create-grid">
                  {manualVisibleAttributes.map((field) => {
                    const rawFieldValue = manualDraft.attributeValues[field.key] ?? (field.type === 'boolean' ? false : '');
                    const fieldValue = field.type === 'location' && rawFieldValue && typeof rawFieldValue === 'object'
                      ? (rawFieldValue.address || rawFieldValue.formatted_address || rawFieldValue.formattedAddress || '')
                      : rawFieldValue;
                    const isWide = field.type === 'text' || field.type === 'location';
                    if (field.type === 'boolean') {
                      return (
                        <label key={field.key} className="checkbox-field inquiries-create-checkbox">
                          <input
                            type="checkbox"
                            checked={Boolean(fieldValue)}
                            onChange={(event) => updateManualAttribute(field, event.target.checked)}
                            disabled={manualSaving}
                          />
                          <span>{field.label || field.key}</span>
                        </label>
                      );
                    }
                    if (field.type === 'select') {
                      return (
                        <label key={field.key} className="inquiry-detail-control">
                          <span>{field.label || field.key}</span>
                          <select
                            className="select"
                            value={fieldValue}
                            onChange={(event) => updateManualAttribute(field, event.target.value)}
                            disabled={manualSaving}
                          >
                            <option value="">Velg</option>
                            {(Array.isArray(field.options) ? field.options : []).map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </label>
                      );
                    }
                    return (
                      <label
                        key={field.key}
                        className={`inquiry-detail-control${isWide ? ' is-full-width' : ''}`}
                      >
                        <span>{field.label || field.key}</span>
                        {field.type === 'text' ? (
                          <textarea
                            className="input inquiries-note-textarea"
                            value={fieldValue}
                            onChange={(event) => updateManualAttribute(field, event.target.value)}
                            disabled={manualSaving}
                          />
                        ) : field.type === 'location' ? (
                          <LocationInput
                            value={rawFieldValue}
                            onChange={(nextValue) => updateManualAttribute(field, nextValue)}
                            disabled={manualSaving}
                            aria-label={field.label || field.key}
                          />
                        ) : (
                          <input
                            className="input"
                            type={field.type === 'integer' ? 'number' : field.type === 'datetime' ? 'datetime-local' : field.type === 'date' ? 'date' : 'text'}
                            value={fieldValue}
                            onChange={(event) => updateManualAttribute(field, event.target.value)}
                            disabled={manualSaving}
                          />
                        )}
                      </label>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </div>

          <footer className="modal-footer">
            <button
              type="button"
              className="button button-secondary"
              onClick={closeManualInquiryModal}
              disabled={manualSaving}
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="button button-primary"
              disabled={manualSaving || manualLoading}
            >
              {manualSaving ? 'Oppretter…' : 'Opprett henvendelse'}
            </button>
          </footer>
        </form>
      </div>,
      document.body,
    )
      : null;

  const filterModal = isFilterModalOpen && typeof document !== 'undefined'
    ? createPortal(
      <div
        className="modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Filtre"
        onClick={() => setIsFilterModalOpen(false)}
      >
        <div
          className="modal modal--sm inquiries-filter-modal"
          onClick={(event) => event.stopPropagation()}
        >
          <header className="modal-header">
            <div className="modal-title">
              <h2>Filtre</h2>
            </div>
            <button
              type="button"
              className="modal-close"
              onClick={() => setIsFilterModalOpen(false)}
              aria-label="Lukk"
            >
              ×
            </button>
          </header>

          <div className="modal-body">
            <form className="inquiries-filter-modal-form" onSubmit={(event) => { event.preventDefault(); handleSearchSubmit(event); setIsFilterModalOpen(false); }}>
              <div className="filter-field">
                <label htmlFor="inquiries-mobile-stage-filter">Status</label>
                <select
                  id="inquiries-mobile-stage-filter"
                  className="select select--sm"
                  value={stageFilter}
                  onChange={(event) => {
                    setStageFilter(event.target.value);
                    setPage(0);
                  }}
                >
                  <option value="alle">Alle</option>
                  <option value="new">Ny</option>
                  <option value="in_progress">Pågår</option>
                  <option value="resolved">Løst</option>
                </select>
              </div>

              <div className="filter-field">
                <label htmlFor="inquiries-mobile-source-filter">Kilde</label>
                <select
                  id="inquiries-mobile-source-filter"
                  className="select select--sm"
                  value={sourceFilter}
                  onChange={(event) => {
                    setSourceFilter(event.target.value);
                    setPage(0);
                  }}
                >
                  <option value="alle">Alle kilder</option>
                  {inquirySources.map((entry) => (
                    <option key={entry.source} value={entry.source}>
                      {entry.label || formatInboxSourceLabel(entry.source)} ({entry.total})
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label htmlFor="inquiries-mobile-assignee-filter">Ansvarlig</label>
                <select
                  id="inquiries-mobile-assignee-filter"
                  className="select select--sm"
                  value={assigneeFilter}
                  onChange={(event) => {
                    setAssigneeFilter(event.target.value);
                    setPage(0);
                  }}
                >
                  <option value="alle">Alle</option>
                  <option value="unassigned">Ufordelt</option>
                  {assigneeFilter === 'me' ? <option value="me">Meg</option> : null}
                  {summaryAssignees.map((assignee) => (
                    <option key={assignee.account_id} value={assignee.account_id}>
                      {assignee.display_name || assignee.email}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label htmlFor="inquiries-mobile-search">Søk</label>
                <div className="inquiries-search-control">
                  <input
                    id="inquiries-mobile-search"
                    className="input input--sm"
                    type="search"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Søk i henvendelser"
                  />
                  <button type="submit" className="button button-secondary button-small">
                    Søk
                  </button>
                </div>
              </div>

              <label className="checkbox-field inquiries-filter-checkbox">
                <input
                  type="checkbox"
                  checked={unreadOnly}
                  onChange={(event) => {
                    setUnreadOnly(event.target.checked);
                    setPage(0);
                  }}
                />
                <span>Kun uleste</span>
              </label>
            </form>
          </div>

          <footer className="modal-footer">
            <button
              type="button"
              className="button button-primary"
              onClick={() => { handleSearchSubmit({ preventDefault: () => {} }); setIsFilterModalOpen(false); }}
            >
              Bruk filtre
            </button>
          </footer>
        </div>
      </div>,
      document.body,
    )
    : null;

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  if (!companyId) {
    return (
      <div className="page">
        <div className="card">
          <h1>Henvendelser</h1>
          <p>Velg et selskap for å se henvendelser.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page inquiries-page">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Innsikt</p>
          <h1>Henvendelser</h1>
          <p className="page-subtitle">
            Samle alle innkommende henvendelser i én inbox, fordel ansvar og åpne hver sak i en egen delbar visning.
          </p>
        </div>
        {canManageInbox ? (
          <div className="page-actions">
            <button type="button" className="button button-primary" onClick={openManualInquiryModal}>
              Ny henvendelse
            </button>
          </div>
        ) : null}
      </div>

      <article className="card conversations-card inquiries-results-card">
        <div className="inquiries-results-top">
          <header className="card-header inquiries-results-header">
            <div>
              <h2>Inbox</h2>
              <p className="card-subtitle">
                {summaryLoading ? 'Oppdaterer oversikt…' : `${inquiriesTotal} henvendelser matcher filtrene`}
              </p>
            </div>
          </header>

          <section className="inquiries-summary-grid" aria-label="Oppsummering av henvendelser">
            <SummaryCard label="Totalt" value={inquiryTotals.total} />
            <SummaryCard label="Uleste" value={inquiryTotals.unread} tone="attention" />
            <SummaryCard label="Nye" value={inquiryTotals.new} />
            <SummaryCard label="Pågår" value={inquiryTotals.in_progress} tone="progress" />
            <SummaryCard label="Løst" value={inquiryTotals.resolved} tone="success" />
            <SummaryCard label="Ufordelte" value={inquiryTotals.unassigned} tone="danger" />
          </section>

          <section className="inquiries-quick-filters" aria-label="Hurtigfiltre">
            <span className="inquiries-quick-filters-label">Hurtigfiltre</span>
            <div className="inquiries-quick-filter-list">
              <QuickFilterButton
                label="Alle saker"
                active={stageFilter === 'alle' && assigneeFilter === 'alle' && !botProductFilter && !columnFilters.product}
                onClick={handleClearQuickFilters}
              />
              <QuickFilterButton
                label="Nye saker"
                active={stageFilter === 'new'}
                count={inquiryTotals.new}
                onClick={handleNewQuickFilter}
              />
              <QuickFilterButton
                label="Mine saker"
                active={assigneeFilter === 'me'}
                count={summaryAssignees.find((assignee) => assignee.account_id === 'me')?.total}
                onClick={handleMineQuickFilter}
              />
              {summaryProducts.map((product) => {
                const productId = Number(product.product_id ?? product.id ?? 0) || null;
                const productName = product.name || product.product_name || 'Uten produkt';
                const active = productId
                  ? botProductFilter === String(productId)
                  : columnFilters.product?.value === productName;
                return (
                  <QuickFilterButton
                    key={productId ? `product-${productId}` : `product-${productName}`}
                    label={productName}
                    color={product.color_code || product.colorCode || null}
                    count={product.total}
                    active={active}
                    onClick={() => handleProductQuickFilter(product)}
                  />
                );
              })}
            </div>
          </section>

          {!isMobileView ? (
          <form className="filter-row inquiries-results-filters" onSubmit={handleSearchSubmit}>
            <div className="filter-field">
              <label htmlFor="inquiries-stage-filter">Status</label>
              <select
                id="inquiries-stage-filter"
                className="select select--sm"
                value={stageFilter}
                onChange={(event) => {
                  setStageFilter(event.target.value);
                  setPage(0);
                }}
              >
                <option value="alle">Alle</option>
                <option value="new">Ny</option>
                <option value="in_progress">Pågår</option>
                <option value="resolved">Løst</option>
              </select>
            </div>

            <div className="filter-field">
              <label htmlFor="inquiries-source-filter">Kilde</label>
              <select
                id="inquiries-source-filter"
                className="select select--sm"
                value={sourceFilter}
                onChange={(event) => {
                  setSourceFilter(event.target.value);
                  setPage(0);
                }}
              >
                <option value="alle">Alle kilder</option>
                {inquirySources.map((entry) => (
                  <option key={entry.source} value={entry.source}>
                    {entry.label || formatInboxSourceLabel(entry.source)} ({entry.total})
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-field">
              <label htmlFor="inquiries-assignee-filter">Ansvarlig</label>
              <select
                id="inquiries-assignee-filter"
                className="select select--sm"
                value={assigneeFilter}
                onChange={(event) => {
                  setAssigneeFilter(event.target.value);
                  setPage(0);
                }}
              >
                <option value="alle">Alle</option>
                <option value="unassigned">Ufordelt</option>
                {assigneeFilter === 'me' ? <option value="me">Meg</option> : null}
                {summaryAssignees.map((assignee) => (
                  <option key={assignee.account_id} value={assignee.account_id}>
                    {assignee.display_name || assignee.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-field inquiries-search-field">
              <label htmlFor="inquiries-search">Søk</label>
              <div className="inquiries-search-control">
                <input
                  id="inquiries-search"
                  className="input input--sm"
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Søk i henvendelser"
                />
                <button type="submit" className="button button-secondary button-small">
                  Søk
                </button>
              </div>
            </div>

            <div className="filter-field inquiries-filter-checkbox">
              <label htmlFor="inquiries-unread-filter">Ulest</label>
              <div className="inquiries-filter-checkbox-row">
                <input
                  id="inquiries-unread-filter"
                  type="checkbox"
                  checked={unreadOnly}
                  onChange={(event) => {
                    setUnreadOnly(event.target.checked);
                    setPage(0);
                  }}
                />
                <span>Kun uleste</span>
              </div>
            </div>

            <div className="filter-field inquiries-rearrange-field">
              <div className={`inquiries-column-config ${columnConfigOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="button button-secondary inquiries-rearrange-button"
                  aria-expanded={columnConfigOpen}
                  aria-haspopup="dialog"
                  onClick={() => setColumnConfigOpen((open) => !open)}
                >
                  Re-arrange
                </button>
                {columnConfigOpen ? (
                  <div className="inquiries-column-config-panel" role="dialog" aria-label="Re-arrange columns">
                    <p className="input-hint">
                      Dra kolonnene for å endre rekkefølgen. Status og Saksbehandler er låst først. Oppsettet lagres kun for din konto.
                    </p>

                    {tableColumns.length === 0 ? (
                      <p className="run-no-answers">Ingen kolonner tilgjengelig ennå.</p>
                    ) : (
                      <div className="inquiries-column-list inquiries-column-list--reorder">
                        {tableColumns.map((column) => {
                          const isPinnedColumn = FIXED_LEADING_COLUMN_IDS.includes(column.id);
                          return (
                            <div
                              key={column.id}
                              ref={(node) => {
                                if (node) {
                                  columnReorderRowRefs.current.set(column.id, node);
                                } else {
                                  columnReorderRowRefs.current.delete(column.id);
                                }
                              }}
                              className={`inquiries-column-option inquiries-column-option--reorder${isPinnedColumn ? ' is-pinned' : ''}${draggedColumnId === column.id ? ' is-dragging' : ''}`}
                              draggable={!isPinnedColumn}
                              onDragStart={(event) => handleColumnDragStart(event, column.id)}
                              onDragOver={(event) => handleColumnDragOver(event, column.id)}
                              onDrop={(event) => handleColumnDrop(event, column.id)}
                              onDragEnd={() => setDraggedColumnId(null)}
                            >
                              <span className="inquiries-column-drag-handle" aria-hidden="true">
                                <span />
                                <span />
                                <span />
                              </span>
                              <label>
                                <input
                                  type="checkbox"
                                  checked
                                  disabled={isPinnedColumn}
                                  onChange={() => toggleColumnDraft(column.id)}
                                />
                                <span>{column.label}</span>
                              </label>
                              <small>{isPinnedColumn ? 'Låst fast kolonne' : column.kind === 'extra' ? 'Ekstrafelt' : 'Synlig'}</small>
                            </div>
                          );
                        })}
                        {hiddenRearrangeColumns.map((column) => (
                          <div key={column.id} className="inquiries-column-option inquiries-column-option--reorder is-hidden-column">
                            <span className="inquiries-column-drag-handle" aria-hidden="true">
                              <span />
                              <span />
                              <span />
                            </span>
                            <label>
                              <input
                                type="checkbox"
                                checked={false}
                                onChange={() => toggleColumnDraft(column.id)}
                              />
                              <span>{column.label}</span>
                            </label>
                            <small>Skjult</small>
                          </div>
                        ))}
                      </div>
                    )}

                    {columnConfigError ? <p className="inquiries-note-error">{columnConfigError}</p> : null}

                    <div className="inquiries-column-actions">
                      <button
                        type="button"
                        className="button button-secondary"
                        onClick={handleResetColumns}
                        disabled={columnConfigSaving}
                      >
                        Tilbakestill
                      </button>
                      <button
                        type="button"
                        className="button button-primary"
                        onClick={handleSaveColumns}
                        disabled={columnConfigSaving}
                      >
                        {columnConfigSaving ? 'Lagrer…' : 'Lagre'}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </form>
          ) : (
          <div className="inquiries-mobile-filter-bar">
            <button
              type="button"
              className="button button-secondary inquiries-mobile-filter-button"
              onClick={() => setIsFilterModalOpen(true)}
            >
              Filter
            </button>
            <div className="filter-field inquiries-rearrange-field">
              <div className={`inquiries-column-config ${columnConfigOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="button button-secondary inquiries-rearrange-button"
                  aria-expanded={columnConfigOpen}
                  aria-haspopup="dialog"
                  onClick={() => setColumnConfigOpen((open) => !open)}
                >
                  Re-arrange
                </button>
                {columnConfigOpen ? (
                  <div className="inquiries-column-config-panel" role="dialog" aria-label="Re-arrange columns">
                    <p className="input-hint">
                      Dra kolonnene for å endre rekkefølgen. Status og Saksbehandler er låst først. Oppsettet lagres kun for din konto.
                    </p>

                    {tableColumns.length === 0 ? (
                      <p className="run-no-answers">Ingen kolonner tilgjengelig ennå.</p>
                    ) : (
                      <div className="inquiries-column-list inquiries-column-list--reorder">
                        {tableColumns.map((column) => {
                          const isPinnedColumn = FIXED_LEADING_COLUMN_IDS.includes(column.id);
                          return (
                            <div
                              key={column.id}
                              ref={(node) => {
                                if (node) {
                                  columnReorderRowRefs.current.set(column.id, node);
                                } else {
                                  columnReorderRowRefs.current.delete(column.id);
                                }
                              }}
                              className={`inquiries-column-option inquiries-column-option--reorder${isPinnedColumn ? ' is-pinned' : ''}${draggedColumnId === column.id ? ' is-dragging' : ''}`}
                              draggable={!isPinnedColumn}
                              onDragStart={(event) => handleColumnDragStart(event, column.id)}
                              onDragOver={(event) => handleColumnDragOver(event, column.id)}
                              onDrop={(event) => handleColumnDrop(event, column.id)}
                              onDragEnd={() => setDraggedColumnId(null)}
                            >
                              <span className="inquiries-column-drag-handle" aria-hidden="true">
                                <span />
                                <span />
                                <span />
                              </span>
                              <label>
                                <input
                                  type="checkbox"
                                  checked
                                  disabled={isPinnedColumn}
                                  onChange={() => toggleColumnDraft(column.id)}
                                />
                                <span>{column.label}</span>
                              </label>
                              <small>{isPinnedColumn ? 'Låst fast kolonne' : column.kind === 'extra' ? 'Ekstrafelt' : 'Synlig'}</small>
                            </div>
                          );
                        })}
                        {hiddenRearrangeColumns.map((column) => (
                          <div key={column.id} className="inquiries-column-option inquiries-column-option--reorder is-hidden-column">
                            <span className="inquiries-column-drag-handle" aria-hidden="true">
                              <span />
                              <span />
                              <span />
                            </span>
                            <label>
                              <input
                                type="checkbox"
                                checked={false}
                                onChange={() => toggleColumnDraft(column.id)}
                              />
                              <span>{column.label}</span>
                            </label>
                            <small>Skjult</small>
                          </div>
                        ))}
                      </div>
                    )}

                    {columnConfigError ? <p className="inquiries-note-error">{columnConfigError}</p> : null}

                    <div className="inquiries-column-actions">
                      <button
                        type="button"
                        className="button button-secondary"
                        onClick={handleResetColumns}
                        disabled={columnConfigSaving}
                      >
                        Tilbakestill
                      </button>
                      <button
                        type="button"
                        className="button button-primary"
                        onClick={handleSaveColumns}
                        disabled={columnConfigSaving}
                      >
                        {columnConfigSaving ? 'Lagrer…' : 'Lagre'}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          )}
        </div>

        {inquiryLoading ? (
          <div className="inquiries-empty-state">
            <div className="loading-spinner" />
            <p>Laster henvendelser…</p>
          </div>
        ) : tableColumns.length === 0 ? (
          <div className="inquiries-empty-state">
            <p>Ingen kolonner er valgt for visning.</p>
            <p className="input-hint">Åpne kolonnepanelet og velg minst én kolonne.</p>
          </div>
        ) : (
          <>
            <div
              ref={tableShellRef}
              className="inquiries-table-shell inquiries-table-shell--card"
              style={expandedViewportWidth ? { '--inquiries-expanded-viewport-width': `${expandedViewportWidth}px` } : undefined}
            >
              <table className="inquiries-table">
                <thead>
                  <tr>
                    {tableColumns.map((column, index) => {
                      const columnClassName = getColumnClassName(column);
                      const isActiveSortColumn = sortBy === column.id;
                      const columnFilter = columnFilters[column.id] || null;
                      const filterVariant = getColumnFilterVariant(column, inquiries);
                      const filterOptions = getColumnFilterOptions(column, inquiries, summaryAssignees, summaryProducts, summaryProgressionStages);
                      return (
                        <th
                          key={column.id}
                          scope="col"
                          aria-sort={isActiveSortColumn ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                          className={[
                            columnClassName,
                            index === 0 ? 'inquiries-column-sticky-left' : '',
                          ].filter(Boolean).join(' ') || undefined}
                        >
                          <div className="inquiries-column-head">
                            <button
                              type="button"
                              className={`inquiries-sort-button ${isActiveSortColumn ? 'is-active' : ''}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSortColumn(column.id);
                              }}
                            >
                              <span>{column.label}</span>
                              <span className="inquiries-sort-indicator" aria-hidden="true">
                                {isActiveSortColumn ? <SortArrowIcon direction={sortDirection} /> : null}
                              </span>
                            </button>
                            <ColumnFilterMenu
                              column={column}
                              variant={filterVariant}
                              options={filterOptions}
                              isOpen={activeColumnFilter === column.id}
                              hasActiveFilter={Boolean(columnFilter)}
                              draftValue={activeColumnFilter === column.id ? columnFilterDraft : String(columnFilter?.value || '')}
                              activeValue={String(columnFilter?.value || '')}
                              activeMode={columnFilter?.mode || 'equals'}
                              onOpen={(event) => handleOpenColumnFilter(event, column.id)}
                              onDraftChange={setColumnFilterDraft}
                              onApply={() => applyColumnFilter(column.id, columnFilterDraft, 'contains')}
                              onClear={() => clearColumnFilter(column.id)}
                              onSelect={(value, mode) => applyColumnFilter(column.id, value, mode)}
                            />
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {displayedInquiries.length === 0 ? (
                    <tr>
                      <td colSpan={tableColumns.length} className="inquiries-table-empty-cell">
                        <div className="inquiries-empty-state inquiries-empty-state--in-table">
                          <p>Ingen henvendelser matcher filtrene.</p>
                          <p className="input-hint">
                            Nye innsendinger fra `submit_inquiry.py` og andre kilder vises her så snart de lagres i `inbox`.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : displayedInquiries.map((inquiry) => {
                    const isExpanded = expandedInquiryIds.has(inquiry.id);
                    const inquiryLabel = inquiry.display_name || inquiry.name || inquiry.ai_summary || inquiry.subject || inquiry.id;
                    return (
                      <React.Fragment key={inquiry.id}>
                        <tr
                          className={[
                            'inquiries-table-row',
                            inquiry.is_read ? 'is-read' : 'is-unread',
                            isExpanded ? 'is-expanded' : '',
                            recentInquiryIds.has(Number(inquiry.id)) ? 'is-live-new' : '',
                          ].filter(Boolean).join(' ')}
                          onClick={() => handleOpenInquiry(inquiry.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleOpenInquiry(inquiry.id);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={`Åpne henvendelse ${inquiryLabel}`}
                        >
                          {tableColumns.map((column, index) => {
                            const rawValue = getInquiryFieldValue(inquiry, column);
                            const plainValue = stringifyValue(rawValue);
                            const isPrimaryCell = index === 0;
                            const columnClassName = getColumnClassName(column);
                            const inlineKey = `${inquiry.id}:${column.id}`;
                            const isUpdatingThisCell = inlineUpdateKey === inlineKey;
                            const editingThisCell = isEditingCell(inquiry.id, column.id);
                            const selectAttributeField = getInquiryAttributeDefinition(inquiry, column);
                            const canEditSelectAttribute = canManageInbox && isEditableSelectAttribute(inquiry, column);

                            let content = plainValue;
                            if (column.id === 'stage') {
                              const stageMeta = getComputedStageMeta(inquiry.stage, inquiry.progression);
                              const statusOptions = getInquiryStatusMenuOptions(inquiry);
                              const hasCustomStatusOptions = getProgressionCustomStatuses(inquiry.progression).length > 0;
                              const stageEditable = canManageInbox && (!inquiry.progression?.configured || hasCustomStatusOptions);
                              const hasProgress = hasVisibleProgression(inquiry.progression);
                              const stagePill = stageEditable ? (
                                <InlineOptionMenu
                                  label={stageMeta.label}
                                  buttonClassName={getStageProgressPillClassName('inquiry-status inquiry-pill-button', stageMeta.className, inquiry.progression)}
                                  disabled={Boolean(inlineUpdateKey)}
                                  isOpen={editingThisCell}
                                  isLoading={isUpdatingThisCell}
                                  options={statusOptions}
                                  selectedValue={getInquirySelectedStatusValue(inquiry)}
                                  onOpen={(event) => handleStartInlineEdit(event, inquiry.id, column.id)}
                                  onSelect={(nextStatusValue) => {
                                    const selectedValue = String(nextStatusValue || '');
                                    if (selectedValue === getInquirySelectedStatusValue(inquiry)) {
                                      setEditingCell(null);
                                      return;
                                    }
                                    if (hasCustomStatusOptions) {
                                      handleInlineUpdate(inquiry.id, column.id, {
                                        customStatusKey: selectedValue.startsWith(CUSTOM_STATUS_VALUE_PREFIX)
                                          ? selectedValue.slice(CUSTOM_STATUS_VALUE_PREFIX.length)
                                          : null,
                                      });
                                    } else {
                                      handleInlineUpdate(inquiry.id, column.id, { stage: selectedValue });
                                    }
                                  }}
                                >
                                  <StageProgressPillContent label={stageMeta.label} progression={inquiry.progression} />
                                </InlineOptionMenu>
                              ) : (
                                <span className={getStageProgressPillClassName('inquiry-status', stageMeta.className, inquiry.progression)}>
                                  <StageProgressPillContent label={stageMeta.label} progression={inquiry.progression} />
                                </span>
                              );
                              content = (
                                <span className={`inquiry-stage-cell-content${hasProgress ? ' has-progress' : ' is-compact'}`}>
                                  <span className="inquiry-stage-main">{stagePill}</span>
                                </span>
                              );
                            } else if (column.id === 'assigned_to') {
                              const assignee = getInquiryAssignee(inquiry, {
                                unassignedLabel: canManageInbox ? 'Tildel' : 'Ufordelt',
                              });
                              const assigneeOptions = [
                                { value: '', label: 'Ufordelt' },
                                ...summaryAssignees.map((assignee) => ({
                                  value: assignee.account_id,
                                  label: assignee.display_name || assignee.email,
                                })),
                              ];
                              content = canManageInbox ? (
                                <InlineOptionMenu
                                  label={assignee.label}
                                  buttonClassName={`inquiry-stage-assignee-pill inquiry-pill-button${assignee.assigned ? '' : ' is-unassigned'}`}
                                  disabled={Boolean(inlineUpdateKey)}
                                  isOpen={editingThisCell}
                                  isLoading={isUpdatingThisCell}
                                  options={assigneeOptions}
                                  selectedValue={inquiry.assigned_to || ''}
                                  onOpen={(event) => handleStartInlineEdit(event, inquiry.id, column.id)}
                                  onSelect={(nextAssignedToValue) => {
                                    const nextAssignedTo = nextAssignedToValue || null;
                                    if ((nextAssignedTo || null) === (inquiry.assigned_to || null)) {
                                      setEditingCell(null);
                                      return;
                                    }
                                    handleInlineUpdate(inquiry.id, column.id, { assignedAccountId: nextAssignedTo });
                                  }}
                                >
                                  <AssigneePillContent assignee={assignee} />
                                </InlineOptionMenu>
                              ) : (
                                <span className={`inquiry-stage-assignee-pill${assignee.assigned ? '' : ' is-unassigned'}`}>
                                  <AssigneePillContent assignee={assignee} />
                                </span>
                              );
                            } else if (column.id === 'chat_id') {
                              content = inquiry.chat_id ? <code>{inquiry.chat_id}</code> : '—';
                            } else if (['submitted_at', 'created_at', 'updated_at', 'last_activity_at'].includes(column.id)) {
                              content = formatDateTime(rawValue);
                            } else if (column.id === 'source') {
                              content = formatInboxSourceLabel(rawValue);
                            } else if (column.id === SUMMARY_COLUMN_ID) {
                              content = (
                                <span className="inquiries-cell-truncate" title={plainValue}>
                                  {plainValue}
                                </span>
                              );
                            } else if (column.kind === 'attribute' && canEditSelectAttribute) {
                              const selectOptions = [
                                { value: '', label: 'Velg' },
                                ...selectAttributeField.options.map((option) => ({
                                  value: option,
                                  label: option,
                                })),
                              ];
                              content = (
                                <InlineOptionMenu
                                  label={plainValue}
                                  buttonClassName="inquiry-chip inquiry-pill-button inquiries-select-cell-button"
                                  disabled={Boolean(inlineUpdateKey)}
                                  isOpen={editingThisCell}
                                  isLoading={isUpdatingThisCell}
                                  options={selectOptions}
                                  selectedValue={String(rawValue ?? '')}
                                  onOpen={(event) => handleStartInlineEdit(event, inquiry.id, column.id)}
                                  onSelect={(nextValue) => {
                                    if (nextValue === String(rawValue ?? '')) {
                                      setEditingCell(null);
                                      return;
                                    }
                                    handleInlineUpdate(inquiry.id, column.id, {
                                      attributeValues: {
                                        [column.key]: nextValue,
                                      },
                                    });
                                  }}
                                />
                              );
                            }

                            return (
                              <td
                                key={column.id}
                                title={column.id === SUMMARY_COLUMN_ID ? undefined : plainValue}
                                className={[
                                  columnClassName,
                                  isPrimaryCell ? 'inquiries-column-sticky-left inquiries-cell-sticky-left' : '',
                                ].filter(Boolean).join(' ') || undefined}
                              >
                                {isPrimaryCell ? (
                                  <div
                                    className={[
                                      'inquiries-cell-primary',
                                      column.id === 'stage' ? 'is-stage-primary' : '',
                                      column.id === 'stage' && hasVisibleProgression(inquiry.progression) ? 'has-stage-progress' : '',
                                    ].filter(Boolean).join(' ')}
                                  >
                                    <button
                                      type="button"
                                      className="inquiries-row-expand-button"
                                      onClick={(event) => toggleInquiryExpansion(event, inquiry.id)}
                                      onKeyDown={stopRowNavigation}
                                      aria-label={`${isExpanded ? 'Skjul' : 'Vis'} detaljer for ${inquiryLabel}`}
                                      aria-expanded={isExpanded}
                                    >
                                      <ExpandRowIcon expanded={isExpanded} />
                                    </button>
                                    <div className={`status-dot is-${getInquiryStatusDotTone(inquiry)}`} />
                                    <span>{content}</span>
                                  </div>
                                ) : content}
                              </td>
                            );
                          })}
                        </tr>
                        {isExpanded ? (
                          <tr className="inquiries-expanded-row" aria-label={`Detaljer for ${inquiryLabel}`}>
                            <td colSpan={tableColumns.length}>
                              <div className="inquiries-expanded-viewport">
                                <InquiryExpandedDetails
                                  inquiry={inquiry}
                                  companyId={companyId}
                                  canManageInbox={canManageInbox}
                                  assignees={summaryAssignees}
                                  onInquiryUpdated={(nextInquiry) => {
                                    setInquiries((current) => current.map((entry) => (
                                      entry.id === nextInquiry.id ? { ...entry, ...nextInquiry } : entry
                                    )));
                                  }}
                                  onOpenInquiry={handleOpenInquiry}
                                />
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 ? (
              <div className="inquiries-pagination">
                <button
                  type="button"
                  className="button button-secondary"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                >
                  Forrige
                </button>
                <span className="pagination-info">
                  Side {page + 1} av {totalPages}
                </span>
                <button
                  type="button"
                  className="button button-secondary"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Neste
                </button>
              </div>
            ) : null}
          </>
        )}
      </article>
      {manualModal}
      {filterModal}
    </div>
  );
};

export default InquiriesPage;
